import { createElement, generateElementId, useTextFieldIdCounter } from '@/helper/element-factory'
import {
  APP_STORE_META,
  AppStore,
  DOCUMENT_LINKED_VARIABLE,
  DOCUMENT_SENDER_ID,
  DocumentStatus,
  ELEMENTS_LOOKUP,
  ELEMENTS_META,
  ElementTypes,
  GROUP_LOOKUP,
  GroupFields,
  GroupFieldsOptions,
  ICustomFieldsLink,
  ICustomFieldsLinkItem,
  IElement,
  IPage,
  IRecipient,
  IResponsiveStylesOptions,
  IUpdateElementOption,
  IUpdateGroupOption,
  IUpdateSectionElementOption,
  LineItem,
  MAX_HEADER_FOOTER_COMBINED_HEIGHT,
  ObjectForLinkedField,
  PAGE_SECTIONS,
  PriceTypeEnum,
  ProductListElementOptions,
  RecipientEntityTypeEnum,
  UpdateSignatureParams,
  applyBottomPreferPositionSync,
  assignableFields,
  bottomPreferLayoutNeedsWrite,
  buildHeaderFooterCloneIdMapFromPages,
  checkForDuplicatesFieldKeyInCustomValueLinkage,
  cloneGroupWithNewIds,
  cloneSectionWithStableFillableIds,
  cloneWithNewIds,
  collectBottomPreferDraggableIdsFromPage,
  computeBottomPreferGeometryFromStoredLayout,
  elementIdInSubtree,
  fillableFields,
  findElementOnSinglePage,
  flatChildrens,
  floatingElementsSet,
  getAdditionalRecipientsWithRoles,
  getFloatingBoundSectionForElementOnPage,
  getRecipientParamsFromContact,
  listHeaderFooterReplicaTargets,
  recomputePageLevelFloatingPositionsForContentResize,
  remapKeysForCVLinkage,
  removeFillableElementsWhichIsPartOfGroup,
  removeUserFromRecipientIfElementIsNotAssigned,
  treeLevelContainsElementId,
  updateRecipientRolesIfElementIsAssigned,
} from '@gohighlevel/ghl-proposals-common'
import { cloneDeep, debounce } from 'lodash'

import { useElementOption } from '@/composable/elementOptions'
import { Editor } from '@tiptap/vue-3'
import { defineStore } from 'pinia'
import { nextTick } from 'vue'
import { useAppStore } from './app'

const DEBOUNCE_DELAY = 300
// Short enough to feel instant to the user (typical typing pause is ~200-300ms) while still
// batching rapid keystrokes. Was 3s previously — which routinely caused changes made on the
// host page's header/footer text block to be missed on other pages when the user hit save,
// switched tabs, or otherwise left the editor within the debounce window.
const SECTION_PROPAGATION_DEBOUNCE_DELAY = 300

const debouncedCreateRedoStack = debounce(function (this: any) {
  this.createRedoStack()
}, DEBOUNCE_DELAY)

// Store pending propagation calls and their debounced functions (clone full section so clone ids are preserved)
const pendingSectionPropagations: Map<
  string,
  {
    elementId: string
    key: string
    value: any
    sectionType: string
    hostPageId: string
    debouncedFn: ReturnType<typeof debounce>
  }
> = new Map()

const createDebouncedPropagateSectionOption = (hostPageId: string, sectionType: string, propagationKey: string) => {
  return debounce(function (this: any) {
    // `updateSectionChangeToEveryPage` already toggles `updateEditors` at the end of the sync so
    // every RichTextEditor / RichTextTableContent watcher fires its content-sync. Do NOT toggle
    // a second time here — two synchronous boolean flips cancel out in Vue's reactivity flush,
    // which is what broke text propagation: the boolean ended up at its previous value by the
    // time the watcher ran, so non-host editors silently skipped `editor.commands.setContent`.
    this.updateSectionChangeToEveryPage(hostPageId, sectionType)
    pendingSectionPropagations.delete(propagationKey)
    scheduleAllPagesFloatingLayoutSync(this)
  }, SECTION_PROPAGATION_DEBOUNCE_DELAY)
}

const floatingLayoutByPageDebouncers = new Map<string, ReturnType<typeof debounce>>()
let floatingLayoutAllPagesDebouncer: ReturnType<typeof debounce> | null = null

function schedulePageFloatingLayoutSync(store: any, pageId: string) {
  let d = floatingLayoutByPageDebouncers.get(pageId)
  if (!d) {
    d = debounce(() => {
      store.recomputeFloatingLayoutPositionsForPage(pageId)
    }, 48)
    floatingLayoutByPageDebouncers.set(pageId, d)
  }
  d()
}

function scheduleAllPagesFloatingLayoutSync(store: any) {
  if (!floatingLayoutAllPagesDebouncer) {
    floatingLayoutAllPagesDebouncer = debounce(() => {
      store.recomputeFloatingLayoutPositionsForAllPages()
    }, 48)
  }
  floatingLayoutAllPagesDebouncer()
}

function shouldScheduleFloatingLayoutAfterOption(key: string): boolean {
  return key !== 'isGhost'
}

function shouldScheduleFloatingLayoutAfterStyle(key: string, elementId: string, pages: IPage[]): boolean {
  if (key === 'position') {
    const el = flatChildrens(pages).find(e => e.id === elementId)
    if (el?.component?.isDraggable) return false
  }
  return true
}

/**
 * Section sync does not target a single block. The whole header (or footer) is the unit:
 * we replace every other page's section with a clone of the host page's section. So all
 * blocks (text, images, etc.) stay in the same order; the "same" image is the one at the
 * same path in the tree. We use that path only to copy height/width when the clone has
 * none (from host, then default 0 as fallback before the image loads).
 */
function syncSectionMediaDimensions(cloneNode: IElement, hostNode: IElement | undefined): void {
  if ((cloneNode.type === ELEMENTS_LOOKUP.IMAGE || cloneNode.type === ELEMENTS_LOOKUP.VIDEO) && cloneNode.responsiveStyles) {
    const hasSrc =
      (cloneNode.type === ELEMENTS_LOOKUP.IMAGE && (cloneNode.component?.options as any)?.src) ||
      (cloneNode.type === ELEMENTS_LOOKUP.VIDEO && (cloneNode.component?.options as any)?.html)
    if (hasSrc) {
      const large = cloneNode.responsiveStyles.large as Record<string, unknown>
      const hostLarge = hostNode?.responsiveStyles?.large as Record<string, unknown> | undefined
      if (large.height == null || large.height === '') {
        large.height = hostLarge?.height != null && hostLarge.height !== '' ? hostLarge.height : 0
      }
      if (large.width == null || large.width === '') {
        large.width = hostLarge?.width != null && hostLarge.width !== '' ? hostLarge.width : 0
      }
    }
  }
  const cloneCh = cloneNode.children
  const hostCh = hostNode?.children
  if (Array.isArray(cloneCh) && Array.isArray(hostCh)) {
    for (let i = 0; i < cloneCh.length && i < hostCh.length; i++) {
      const c = cloneCh[i]
      const h = hostCh[i]
      if (c) syncSectionMediaDimensions(c, h)
    }
  }
}

let shouldCreateNewEditorGroup = true

const { getNextCount } = useTextFieldIdCounter()

export const useBuilderStore = defineStore('builderStore', {
  state: (): AppStore => ({
    activeElementId: '',
    groups: [] as GroupFields<GroupFieldsOptions>[],
    activeGroup: {} as GroupFields<GroupFieldsOptions>,
    editor: undefined,
    pages: [],
    hover: false,
    // Monotonically-incrementing counter used purely as a reactivity signal for RichTextEditor /
    // RichTextTableContent watchers to resync their tiptap instance's content. MUST be a counter
    // (not a boolean toggle) — synchronous toggles can pair up and cancel out in Vue's reactive
    // flush, silently dropping editor refreshes when a flush or batched propagation runs.
    updateEditors: 0 as number,
    focusedElement: {} as IElement,
    headerSelected: false,
    footerSelected: false,
    showHeaderSettings: false,
    isHeaderSectionActive: false,
    isFooterSectionActive: false,
    enablePageNumbering: true,
    pageNumberLocation: 'footer',
    pageNumberFormat: 'number-only',
    undoRedoStack: [],
    undoRedoStackPointer: 0,
    undoSelected: false,
    moveElementDragState: false,
    status: DocumentStatus.DRAFT,
    flatCustomValues: {},
    customValueLinkage: {} as Record<string, ICustomFieldsLink>,
    loadedFonts: [] as Array<string>, // Fonts that have been loaded from the FontFamilyDropdown (list) component - This is for not to load the same font again in the editor
    fontsToLoad: [] as Array<string>, // Fonts that is being used in the editor / entire document - This font will get saved in document for loading in document viewer side
    headerSection: undefined,
    footerSection: undefined,
    headerFooterCloneIdMap: {},
    sectionHeightValidationError: null as string | null,
    /**
     * True while `syncHeaderFooterToAllPages` (the "apply to all pages" fanout for
     * header / footer edits) is running. Bound to <HeaderFooterSyncLoader :show> so
     * users get a blocking overlay during the heavy `this.pages.forEach(...)` work.
     */
    isApplyingHeaderFooterChanges: false,
  }),
  getters: {
    isDocumentEditable: state => {
      return state.status === DocumentStatus.DRAFT
    },
    activeElement: state => {
      const page = state.pages.find(page => {
        return page.id === state.activeElementId
      })
      if (page) return page

      // Check header/footer sections directly first
      for (const pageItem of state.pages) {
        const headerSection = pageItem?.component?.options?.[PAGE_SECTIONS.HEADERS]
        if (headerSection && headerSection.id === state.activeElementId) {
          return headerSection
        }
        const footerSection = pageItem?.component?.options?.[PAGE_SECTIONS.FOOTERS]
        if (footerSection && footerSection.id === state.activeElementId) {
          return footerSection
        }
      }

      // Then check all other elements via flatChildrens
      const activeElement = flatChildrens(state.pages).find((element: IElement) => element?.id === state.activeElementId)
      return activeElement
    },
    hasLineItems: state => {
      return flatChildrens(state.pages).find((element: IElement) => element.type === ELEMENTS_LOOKUP.PRODUCT_LIST)?.id ? true : false
    },
    totalLineItemsLength: state => {
      return flatChildrens(state.pages)
        .filter(
          child => child.type === ELEMENTS_LOOKUP.PRODUCT_LIST && (child as IElement<ProductListElementOptions>).component.options.lineItems
        )
        .map(child => (child as IElement<ProductListElementOptions>).component.options.lineItems.length)
        .reduce((acc, count) => acc + count, 0)
    },
    getAllLineItems: state => {
      return flatChildrens(state.pages)
        .filter(
          child => child.type === ELEMENTS_LOOKUP.PRODUCT_LIST && (child as IElement<ProductListElementOptions>).component.options.lineItems
        )
        .reduce((acc, elem) => {
          acc.push(...elem.component.options.lineItems)
          return acc
        }, [] as LineItem[])
    },
  },
  actions: {
    setStatus(status: DocumentStatus) {
      this.status = status
    },
    findElementInChildren(
      id: string,
      children: any[],
      parent: any,
      parentType: ElementTypes
    ): {
      element: IElement
      parent: IElement
      parentType: ElementTypes
    } | null {
      for (const child of children) {
        if (child && child.id === id) {
          return {
            element: child,
            parent,
            parentType,
          }
        }

        if (child?.children && child?.children?.length > 0) {
          const found = this.findElementInChildren(id, child.children, child, child.type)
          if (found) return found
        }
      }
      return null
    },
    getElementById(id: string) {
      for (const page of this.pages) {
        if (page.id === id) {
          return {
            element: page,
            parent: null,
            parentType: null,
          }
        }

        const found = this.findElementInChildren(id, page.children, page, ELEMENTS_LOOKUP.PAGE)

        if (found) return found

        const headers = page.component?.options?.headers
        const foundInHeaders = this.findElementInChildren(id, headers?.children ?? [], headers, ELEMENTS_LOOKUP.PAGE_SECTION)
        if (foundInHeaders) return foundInHeaders

        const footers = page.component?.options?.footers
        const foundInFooters = this.findElementInChildren(id, footers?.children ?? [], footers, ELEMENTS_LOOKUP.PAGE_SECTION)
        if (foundInFooters) return foundInFooters
      }

      return null
    },
    getPageIdByElementId(elementId: string) {
      // Deep subtree search so the lookup works for elements at ANY nesting depth — not
      // just top-level page.children, but also fillables nested inside Text / Table / Image
      // blocks (page.children[i].children[j]). The previous shallow `treeLevelContainsElementId`
      // only checked the immediate `children` array (with a one-level dip into Row > Column),
      // so a Signature / DateField / TextField fillable embedded inside a Text block returned
      // `undefined` here — which then made `moveFloatingElement`'s
      // `isMovingToDifferentParent` check fire spuriously on every drag (because
      // `newParentId !== undefined` is always true). Using the deep helper fixes that.
      return this.pages.find(
        page =>
          elementIdInSubtree(page.children, elementId) ||
          elementIdInSubtree(page.component?.options?.headers?.children, elementId) ||
          elementIdInSubtree(page.component?.options?.footers?.children, elementId)
      )?.id
    },
    getPageIdBySectionElementId(elementId: string, sectionType: string) {
      // Deep subtree search so the lookup works at any nesting depth — top-level
      // children, ROW > COLUMN children, deeper nested rows from prior split-columns,
      // etc. The earlier shallow `treeLevelContainsElementId` only covered top-level
      // and one level into ROW > COLUMN, which silently missed elements at deeper
      // depths and made `PageSection.onDropElement`'s move-existing branch no-op.
      return this.pages.find(page => elementIdInSubtree(page.component?.options[sectionType]?.children, elementId))?.id
    },
    createRedoStack(skipUnsavedChanges?: boolean) {
      if (!this.isDocumentEditable) return
      this.undoRedoStack.push(cloneDeep(this.pages))
      this.undoRedoStackPointer = this.undoRedoStack.length - 1
      const appStore = useAppStore()
      if (!skipUnsavedChanges) {
        appStore.setUnsavedChanges(true)
        this.undoSelected = false
      }
    },
    undo() {
      if (!this.isDocumentEditable) return
      if (this.undoRedoStackPointer > 0) {
        this.undoRedoStackPointer--
        const page = cloneDeep(this.undoRedoStack[this.undoRedoStackPointer])
        this.pages = page
        // Monotonic signal — see state declaration. Using a counter guarantees the watcher fires
        // on every bump (unlike a boolean, which can cancel out across synchronous toggles).
        this.updateEditors = (this.updateEditors + 1) % 0x7fffffff
        this.undoSelected = true
      }
    },
    redo() {
      if (!this.isDocumentEditable) return
      if (this.undoRedoStackPointer < this.undoRedoStack.length - 1) {
        this.undoRedoStackPointer++
        const page = cloneDeep(this.undoRedoStack[this.undoRedoStackPointer])
        this.pages = page
        this.updateEditors = (this.updateEditors + 1) % 0x7fffffff
      }
    },
    setPages(pages: IPage[]) {
      this.pages = pages
      this.headerFooterCloneIdMap = buildHeaderFooterCloneIdMapFromPages(pages)

      // Resolve header/footer source: use first page if it has section, else first page that has it (e.g. PDF added at front)
      const firstPage = pages[0]
      if (firstPage) {
        const headerSection =
          firstPage.component?.options?.[PAGE_SECTIONS.HEADERS] ??
          pages.find(p => p.component?.options?.[PAGE_SECTIONS.HEADERS])?.component?.options?.[PAGE_SECTIONS.HEADERS]
        const footerSection =
          firstPage.component?.options?.[PAGE_SECTIONS.FOOTERS] ??
          pages.find(p => p.component?.options?.[PAGE_SECTIONS.FOOTERS])?.component?.options?.[PAGE_SECTIONS.FOOTERS]

        if (headerSection) {
          this.headerSection = headerSection
          this.headerSelected = true
        } else {
          this.headerSection = undefined
        }

        if (footerSection) {
          this.footerSection = footerSection
          this.footerSelected = true
        } else {
          this.footerSection = undefined
        }

        // Initialize page numbering options from first page, or set defaults
        const enablePageNumbering = firstPage.component?.options?.enablePageNumbering
        const pageNumberLocation = firstPage.component?.options?.pageNumberLocation
        const pageNumberFormat = firstPage.component?.options?.pageNumberFormat

        // Set defaults if not present
        if (enablePageNumbering === undefined) {
          this.enablePageNumbering = true
          // Apply to all pages
          pages.forEach(page => {
            this.updateElementOption({
              id: page.id,
              key: 'enablePageNumbering',
              value: true,
              type: ELEMENTS_LOOKUP.PAGE,
            })
          })
        } else {
          this.enablePageNumbering = enablePageNumbering
        }

        if (pageNumberLocation === undefined || pageNumberLocation === '') {
          this.pageNumberLocation = 'footer'
          // Apply to all pages
          pages.forEach(page => {
            this.updateElementOption({
              id: page.id,
              key: 'pageNumberLocation',
              value: 'footer',
              type: ELEMENTS_LOOKUP.PAGE,
            })
          })
        } else {
          this.pageNumberLocation = pageNumberLocation
        }

        if (pageNumberFormat === undefined || pageNumberFormat === '') {
          this.pageNumberFormat = 'number-only'
          // Apply to all pages
          pages.forEach(page => {
            this.updateElementOption({
              id: page.id,
              key: 'pageNumberFormat',
              value: 'number-only',
              type: ELEMENTS_LOOKUP.PAGE,
            })
          })
        } else {
          this.pageNumberFormat = pageNumberFormat
        }
      } else {
        this.headerSection = undefined
        this.footerSection = undefined
      }

      if (this.isDocumentEditable && (this.headerSection || this.footerSection)) {
        this.syncHeaderFooterToAllPages()
      }
      this.createRedoStack(true)
    },
    setActiveGroup(groupId: string | null) {
      if (!this.isDocumentEditable) return
      if (!groupId) {
        this.activeGroup = null
      }
      this.activeGroup = this.groups.find(g => g.id === groupId)
      // Recompute section highlight: when an active group is set during
      // multi-select / "Add to group" / drag-group flows, `activeElementId`
      // is null, but the header/footer that contains the group's members
      // should stay highlighted. `updateSectionActivationState` now consults
      // `activeGroup.children` for that, so call it here to apply the change
      // synchronously.
      ;(this as any).updateSectionActivationState()
      this.createRedoStack(true)
    },
    setActiveGroupByElementId(elementId: string) {
      if (!this.isDocumentEditable) return
      const group = this.groups.find(g => g.children.some(c => c.elementId === elementId))
      if (group) {
        this.activeGroup = group
      }
    },
    setGroups(groups: GroupFields<GroupFieldsOptions>[]) {
      this.groups = groups
      this.syncGroup()
      if (!this.isDocumentEditable) return
      this.createRedoStack(true)
    },
    updateGroup(group: GroupFields<GroupFieldsOptions>) {
      if (!this.isDocumentEditable) return
      this.groups = [...this.groups, group]
      this.createRedoStack(true)
    },
    deleteGroupAndGroupElements(id: string) {
      if (!this.isDocumentEditable) return
      const group = this.groups.find(g => g.id === id)
      // Elements inside a header/footer section must be removed via `deleteElementInSection`, not
      // `deleteElement` (which only walks `page.children`). Without this branch, delete-on-group
      // for a checkbox group in a header removes the group record but leaves all the checkboxes
      // on the page — user-visible "nothing happens" behaviour.
      group?.children.forEach(({ elementId }) => {
        const section = this.getElementSection(elementId)
        if (section === PAGE_SECTIONS.HEADERS || section === PAGE_SECTIONS.FOOTERS) {
          this.deleteElementInSection(elementId, section)
        } else {
          this.deleteElement(elementId)
        }
      })
      this.groups = this.groups.filter(g => g.id !== id)
      this.createRedoStack(true)
    },
    deleteGroup(id: string) {
      if (!this.isDocumentEditable) return
      this.groups = this.groups.filter(g => g.id !== id)
      this.createRedoStack(true)
    },
    updateCustomValueLinkage(elementId: string, type: string, customLinkItem: ICustomFieldsLinkItem) {
      if (!this.isDocumentEditable) return
      if (this.customValueLinkage[elementId]) {
        // check the type and push the customLinkItem to the existing customFields array
        const cf = this.customValueLinkage[elementId].customFields
        if (cf && cf.length > 0) {
          const isFieldKeyExist = cf.findIndex(c => c.fieldKey === customLinkItem.fieldKey)
          if (isFieldKeyExist === -1) {
            this.customValueLinkage[elementId].customFields.push(customLinkItem)
          }
        }
      } else {
        const customFields = []
        customFields.push(customLinkItem)
        this.customValueLinkage[elementId] = {
          type,
          customFields,
        }
      }
      this.syncContactIdInLinkedCustomValues()
      this.createRedoStack(true)
    },
    updateCustomValueLinkageItem(elementId: string, addCustomLinkItem: ICustomFieldsLinkItem, removeCustomLinkItem: ICustomFieldsLinkItem) {
      if (this.customValueLinkage[elementId]) {
        const customFields = this.customValueLinkage[elementId].customFields
        const index = customFields.findIndex(item => item.fieldKey === removeCustomLinkItem?.fieldKey)
        if (index !== -1) {
          customFields.splice(index, 1, addCustomLinkItem)
        }
      }
    },
    addNewElementsToCustomValueLinkage(customValueLink: Record<string, ICustomFieldsLink>) {
      if (!this.isDocumentEditable) return
      for (const key in customValueLink) {
        if (!(key in this.customValueLinkage)) {
          this.customValueLinkage[key] = customValueLink[key]
        }
      }
      this.syncContactIdInLinkedCustomValues()
    },
    setCustomValueLinkage(customValueLinkage: Record<string, ICustomFieldsLink>) {
      this.customValueLinkage = customValueLinkage
      if (!this.isDocumentEditable) return
      this.syncContactIdInLinkedCustomValues()
      this.createRedoStack(true)
    },
    deleteCustomValueLinkage(elementId: string, fieldKey: string) {
      if (elementId && this.customValueLinkage[elementId] && fieldKey) {
        const customFields = this.customValueLinkage[elementId].customFields
        if (customFields && customFields.length > 0) {
          const index = customFields.findIndex(c => c.fieldKey === fieldKey)
          if (index > -1) {
            customFields.splice(index, 1)
          }
        }
        if (this.customValueLinkage[elementId].customFields.length === 0) {
          delete this.customValueLinkage[elementId]
        }
        this.createRedoStack(true)
      }
    },
    getCustomValueLinkage(elementId: string) {
      return this.customValueLinkage[elementId]?.customFields || []
    },
    getAllLinkedDocumentVariables() {
      return Object.values(this.customValueLinkage)
        .map(linkage => linkage.customFields)
        .flat()
        .filter(field => field && field?.objectType === ObjectForLinkedField.DOCUMENT_VARIABLE)
        .map(field => {
          return {
            fieldKey: field.fieldKey,
            value: field.value,
            type: DOCUMENT_LINKED_VARIABLE,
          }
        })
    },

    addElementToExistingGroup(elementInfo: { pageId: string; elementId: string }, groupId: string) {
      this.groups?.forEach(g => {
        if (g.id === groupId) {
          g.children.push(elementInfo)
        }
      })
    },
    addElementsToExistingGroup(elementInfo: { pageId: string; elementId: string }[], groupId: string) {
      this.groups?.forEach(g => {
        if (g.id === groupId) {
          const cgMap = g.children.map(({ elementId }) => elementId)
          elementInfo.forEach(({ elementId, pageId }) => {
            if (!cgMap.includes(elementId)) {
              g.children.push({ elementId, pageId })
            }
          })
        }
      })
    },
    getElementGroup(elementId: string) {
      return this.groups.find(g => {
        const cgChild = g.children.map((child: any) => child.elementId)
        if (cgChild.includes(elementId)) {
          return g
        }
      })
    },
    updateOneGroup(groupId: string, children: { pageId: string; elementId: string }[], groupType?: string) {
      if (groupId) {
        this.groups?.forEach(g => {
          if (g.id === groupId) {
            g.children = children
            if (groupType) {
              g.component.options.groupType = groupType
            }
          }
        })
      }
    },
    updateGroupOption(params: IUpdateGroupOption) {
      if (!this.isDocumentEditable) return
      const { id, value, key } = params
      const groupIndex = this.groups.findIndex(g => g.id === id)
      if (groupIndex > -1) {
        const g = this.groups[groupIndex]
        const opts = g.component.options as Record<string, GroupFieldsOptions[keyof GroupFieldsOptions] | number>
        opts[key] = value
      }
      this.createRedoStack()
      return
    },
    updateFocusedElement(element?: IElement) {
      if (!this.isDocumentEditable) return
      this.focusedElement = element
    },
    changeHoverState(state: boolean) {
      if (!this.isDocumentEditable) return
      this.hover = state
    },
    deletePage(pageId: string) {
      if (!this.isDocumentEditable) return
      const pageIndex = this.pages.findIndex(page => page.id === pageId)
      if (pageIndex > -1) {
        this.pages.splice(pageIndex, 1)

        if (pageIndex === 0 && this.pages.length > 0) {
          const newFirstPage = this.pages[0]
          this.headerSection = newFirstPage?.component?.options?.[PAGE_SECTIONS.HEADERS]
          this.footerSection = newFirstPage?.component?.options?.[PAGE_SECTIONS.FOOTERS]
          const activeId = this.activeElementId
          if (activeId) {
            const stillInTree =
              this.pages.some(
                p =>
                  p.component?.options?.[PAGE_SECTIONS.HEADERS]?.id === activeId ||
                  p.component?.options?.[PAGE_SECTIONS.FOOTERS]?.id === activeId
              ) || flatChildrens(this.pages).some((el: IElement) => el?.id === activeId)
            if (!stillInTree) {
              this.updateActiveElement(null)
            }
          }
        }
      }
      this.syncGroup()
      this.syncCustomFieldsForLinkage()
      this.createRedoStack()
    },
    duplicatePage(pageId: string) {
      if (!this.isDocumentEditable) return

      const pageIndex = this.pages.findIndex(page => page.id === pageId)
      if (pageIndex === -1) return

      const page = this.pages[pageIndex]
      const { children, ...newPageKeys } = cloneDeep(page)

      const generateNewChildren = (children: IElement[]): IElement[] =>
        children?.map(child => ({
          ...child,
          id: generateElementId(),
          children: generateNewChildren(child.children),
        }))

      const newChildren = generateNewChildren(children)

      const newPage = {
        ...newPageKeys,
        children: newChildren,
        id: generateElementId(),
        // Strip the cloneDeep'd headers/footers so `syncHeaderFooterToNewPage` will mint
        // fresh per-page ids for the duplicate via the canonical clone-id-map path.
        // Otherwise the duplicate carries the source page's element ids verbatim and any
        // subsequent value/style mirror update collides on duplicate ids across pages.
        component: {
          ...newPageKeys.component,
          options: {
            ...newPageKeys.component?.options,
            [PAGE_SECTIONS.HEADERS]: undefined,
            [PAGE_SECTIONS.FOOTERS]: undefined,
          },
        },
      }

      this.pages.splice(pageIndex + 1, 0, newPage)
      // Only clone the canonical H/F onto the duplicate; existing pages stay untouched.
      this.syncHeaderFooterToNewPage(newPage.id)
      this.createRedoStack()
    },
    deleteElement(elementId: string) {
      if (!this.isDocumentEditable) return

      // Function to recursively search and delete an element inside nested children
      const deleteNestedElement = (children: any[]) => {
        const elementIndex = children.findIndex(child => child?.id === elementId)
        if (elementIndex !== -1) {
          children.splice(elementIndex, 1)
          return true // Element found and deleted
        }

        // Check if any child contains nested children and search recursively
        for (const child of children) {
          if (child?.children?.length) {
            const isDeleted = deleteNestedElement(child.children)
            if (isDeleted) {
              // Only remove empty containers (COLUMN / ROW). Block elements
              // like TEXT / IMAGE / TABLE / VIDEO / PRODUCT_LIST can legitimately
              // exist with zero fillable children, so they must not be
              // cascade-deleted just because their last fillable child was.
              const isContainer = child.type === ELEMENTS_LOOKUP.COLUMN || child.type === ELEMENTS_LOOKUP.ROW
              if (isContainer && child.children.length === 0) {
                const childIndex = children.indexOf(child)
                children.splice(childIndex, 1)
              }
              return true
            }
          }
        }

        return false
      }

      // Iterate over pages to find and delete the element
      const pageIndex = this.pages.findIndex(page =>
        page.children?.some(element => element.id === elementId || deleteNestedElement(element.children || []))
      )

      if (pageIndex > -1) {
        const page = this.pages[pageIndex]

        // Try to delete the element at the top level first
        const elementIndex = page.children?.findIndex(element => element?.id === elementId)

        if (elementIndex !== undefined && elementIndex > -1) {
          const deletedElement = page.children[elementIndex]
          page.children.splice(elementIndex, 1)

          // Check if the deleted element was a COLUMN
          if (deletedElement.type === ELEMENTS_LOOKUP.COLUMN) {
            // If it was a column, check if the parent (row) should be deleted
            const parentRow = page.children.find(child => child.children?.includes(deletedElement))
            if (parentRow && parentRow.children.length === 0) {
              const rowIndex = page.children.indexOf(parentRow)
              page.children.splice(rowIndex, 1)
            }
          }
        } else {
          // If not found at the top level, try to delete in nested elements
          page.children.forEach(child => {
            if (child.children?.length) {
              deleteNestedElement(child.children)
            }
          })
        }

        // Clean up empty columns and rows, and replace row -> column -> element with the element directly
        page.children = page.children.flatMap(child => {
          // If child is a COLUMN and has no children, remove it
          if (child.type === ELEMENTS_LOOKUP.COLUMN && child.children.length === 0) {
            return []
          }

          // If child is a ROW with only one column, and that column has only one child
          if (
            child.type === ELEMENTS_LOOKUP.ROW &&
            child.children.length === 1 &&
            child.children[0].type === ELEMENTS_LOOKUP.COLUMN &&
            child.children[0].children.length === 1
          ) {
            // Replace the row with the element inside the column
            return [child.children[0].children[0]]
          }

          // If child is a ROW and has no children, remove it
          if (child.type === ELEMENTS_LOOKUP.ROW && child.children.length === 0) {
            return []
          }

          return child // Keep this child
        })
      }

      this.createRedoStack()
    },
    deleteElementInSection(elementId: string, sectionType = PAGE_SECTIONS.HEADERS) {
      if (!this.isDocumentEditable) return

      const cloneMap = this.headerFooterCloneIdMap ?? {}

      // Collect element id + all descendants. For non-fillable blocks only: also remove consecutive
      // trailing fillable siblings (they follow the block in column.children). Skip that cascade when
      // deleting a fillable/draggable itself — header/footer often store many independent floats as siblings.
      const addDescendantIds = (el: any, ids: Set<string>) => {
        if (el?.id) ids.add(el.id)
        if (Array.isArray(el?.children)) el.children.forEach((c: any) => addDescendantIds(c, ids))
      }
      const findAndCollectIds = (children: any[], targetId: string): Set<string> | null => {
        if (!children?.length) return null
        for (let i = 0; i < children.length; i++) {
          const child = children[i]
          if (child?.id === targetId) {
            const ids = new Set<string>()
            addDescendantIds(child, ids)
            const deletingFloatingOrDraggable = Boolean(child?.component?.isDraggable) || floatingElementsSet.has(child?.type)
            if (!deletingFloatingOrDraggable) {
              for (let j = i + 1; j < children.length; j++) {
                const sib = children[j]
                if (sib?.id && (sib.component?.isDraggable || floatingElementsSet.has(sib.type))) {
                  ids.add(sib.id)
                  addDescendantIds(sib, ids)
                } else break
              }
            }
            return ids
          }
          const found = findAndCollectIds(child?.children ?? [], targetId)
          if (found) return found
        }
        return null
      }

      let subtreeIds = new Set<string>([elementId])
      for (const page of this.pages) {
        const pageSection = page.component?.options?.[sectionType]
        if (!pageSection?.children?.length) continue
        const found = findAndCollectIds(pageSection.children, elementId)
        if (found) {
          subtreeIds = found
          break
        }
      }

      const idsToDelete = new Set<string>()
      subtreeIds.forEach(id => {
        idsToDelete.add(id)
        const clones = cloneMap[id]
        if (clones) Object.values(clones).forEach(cloneId => idsToDelete.add(cloneId))
      })
      Object.keys(cloneMap).forEach(srcId => {
        if (Object.values(cloneMap[srcId] || {}).some(cloneId => subtreeIds.has(cloneId))) {
          idsToDelete.add(srcId)
          Object.values(cloneMap[srcId] || {}).forEach(id => idsToDelete.add(id))
        }
      })

      const deleteRecursive = (children: any[], ids: Set<string>): boolean => {
        if (!children || !Array.isArray(children)) return false
        const elementIndex = children.findIndex(child => child?.id != null && ids.has(child.id))
        if (elementIndex !== -1) {
          const deletedElement = children[elementIndex]
          children.splice(elementIndex, 1)
          if (deletedElement.type === ELEMENTS_LOOKUP.COLUMN) {
            const parentRow = children.find((c: any) => c.children?.includes(deletedElement))
            if (parentRow && parentRow.children.length === 0) {
              const rowIndex = children.indexOf(parentRow)
              children.splice(rowIndex, 1)
            }
          }
          return true
        }
        for (let i = 0; i < children.length; i++) {
          const child = children[i]
          if (child?.children && Array.isArray(child.children)) {
            if (deleteRecursive(child.children, ids)) {
              // Only remove empty containers (COLUMN / ROW). Block elements
              // like TEXT / IMAGE / TABLE / VIDEO / PRODUCT_LIST can legitimately
              // exist with zero fillable children, so they must not be
              // cascade-deleted just because their last fillable child was.
              const isContainer = child.type === ELEMENTS_LOOKUP.COLUMN || child.type === ELEMENTS_LOOKUP.ROW
              if (isContainer && child.children.length === 0) {
                children.splice(i, 1)
                i--
              }
              return true
            }
          }
        }
        return false
      }

      let elementDeleted = false
      for (const page of this.pages) {
        const pageSection = page.component?.options?.[sectionType]
        if (!pageSection) continue
        if (!pageSection.children) {
          pageSection.children = []
          continue
        }
        while (deleteRecursive(pageSection.children, idsToDelete)) {
          elementDeleted = true
        }
        if (pageSection.children?.length > 0) {
          pageSection.children = pageSection.children.flatMap((child: any) => {
            if (child.type === ELEMENTS_LOOKUP.COLUMN && (!child.children || child.children.length === 0)) return []
            if (
              child.type === ELEMENTS_LOOKUP.ROW &&
              child.children?.length === 1 &&
              child.children[0]?.type === ELEMENTS_LOOKUP.COLUMN &&
              child.children[0]?.children?.length === 1
            )
              return [child.children[0].children[0]]
            if (child.type === ELEMENTS_LOOKUP.ROW && (!child.children || child.children.length === 0)) return []
            return child
          })
        }
      }

      if (elementDeleted) {
        subtreeIds.forEach(id => {
          if (cloneMap[id]) delete cloneMap[id]
          else {
            const sourceId = Object.keys(cloneMap).find(srcId => cloneMap[srcId] && Object.values(cloneMap[srcId]).includes(id))
            if (sourceId) delete cloneMap[sourceId]
          }
        })
        if (idsToDelete.has(this.activeElementId)) {
          this.updateActiveElement(null)
        }
        this.createRedoStack()
      }
    },
    deleteElementFromPage(elementId: string, pageId: string) {
      if (!this.isDocumentEditable) return
      const pageIndex = this.pages.findIndex(page => pageId === page.id)
      if (pageIndex > -1) {
        const page = this.pages[pageIndex]
        const elementIndex = page.children?.findIndex(element => element.id === elementId)
        if (elementIndex !== undefined && elementIndex > -1) {
          page.children?.splice(elementIndex, 1)
        }
      }
      this.createRedoStack()
    },
    cloneElement(elementId: string) {
      if (!this.isDocumentEditable) return
      if (!elementId) return
      let newElement: any

      // Check if element is in a header or footer section
      const sectionType = this.getElementSection(elementId)
      const isInSection = sectionType === PAGE_SECTIONS.HEADERS || sectionType === PAGE_SECTIONS.FOOTERS

      const createNestedClone = (element: IElement, pageIndex: number) => {
        let clone: IElement = {
          ...cloneDeep(element),
          id: generateElementId(),
          children: [],
        }
        if (element.type === ELEMENTS_LOOKUP.TEXT_FIELD) {
          clone = {
            ...clone,
            component: {
              ...clone.component,
              options: {
                ...clone.component.options,
                fieldId: `text_field_${getNextCount()}`,
              },
            },
          }
        }

        if (element.component?.isDraggable) {
          const position = {
            top: (element?.responsiveStyles?.large?.position?.top || 0) + 10,
            left: (element?.responsiveStyles?.large?.position?.left || 0) + 10,
            bottom: (element?.responsiveStyles?.large?.position?.bottom || 0) - 10,
            right: (element?.responsiveStyles?.large?.position?.right || 0) - 10,
          }

          /**
           * TODO: This is a temporary fix to ensure that the checkbox is not placed in the wrong position
           */
          // if (element.type === ELEMENTS_LOOKUP.CHECKBOX) {
          //   const group = this.getElementGroup(elementId)
          //   if (group) {
          //     const pos = getNewPositionForElement(
          //       elementId,
          //       pageIndex,
          //       PositionTypeEnum.BOTTOM_TOP
          //     )
          //     if (pos) {
          //       position = pos
          //     }
          //   }
          // }

          clone = {
            ...clone,
            pageIndex: pageIndex,
            pageId: this.pages[pageIndex]?.id,
            responsiveStyles: {
              ...clone.responsiveStyles,
              large: {
                ...clone.responsiveStyles.large,
                position: {
                  ...clone.responsiveStyles.large.position,
                  ...position,
                },
              },
            },
          }
        }
        return clone
      }

      const findAndCloneElement = (elements: any[], pageIndex: number): boolean => {
        for (let i = 0; i < elements.length; i++) {
          const element = elements[i]

          if (element.id === elementId) {
            newElement = createNestedClone(element, pageIndex)

            elements.splice(i + 1, 0, newElement)
            this.updateActiveElement(newElement)
            return true
          }

          if (element.children) {
            const found = findAndCloneElement(element.children, pageIndex)
            if (found) return true
          }
        }
        return false
      }

      if (isInSection) {
        // Find the page that contains the element in the section
        const pageIndex = this.pages.findIndex(page => {
          const sectionChildren = page.component?.options?.[sectionType as string]?.children || []
          return this.findElementInChildren(elementId, sectionChildren, page, sectionType as ElementTypes)
        })

        if (pageIndex > -1) {
          const page = this.pages[pageIndex]
          const sectionChildren = page.component?.options?.[sectionType as string]?.children || []

          findAndCloneElement(sectionChildren, pageIndex)

          // Propagate the change to all pages
          this.updateSectionChangeToEveryPage(page.id, sectionType as string)
        }
      } else {
        // Original logic for content area
        const pageIndex = this.pages.findIndex(page =>
          page.children?.some(element => {
            if (element.type === ELEMENTS_LOOKUP.ROW) {
              return element.children?.some(child => {
                if (child.type === ELEMENTS_LOOKUP.COLUMN) {
                  return child.children?.some(c => {
                    if (c.id === elementId) return true
                    if (c.children && c.children.length) {
                      return c.children.some(cc => cc.id === elementId)
                    }
                  })
                }
                return child.id === elementId
              })
            } else {
              if (element.id === elementId) return true
              if (element.children && element.children.length) {
                return element.children.some(child => child.id === elementId)
              }
            }
          })
        )
        if (pageIndex > -1) {
          const page = this.pages[pageIndex]
          findAndCloneElement(page.children, pageIndex)
        }
      }

      this.createRedoStack()
      return newElement
    },
    clearEditorInstance() {
      if (!this.isDocumentEditable) return
      this.editor = undefined
    },
    getCommonPageSection(sectionType: string) {
      if (!this.pages[0]?.component?.options[sectionType]) return
      return this.pages[0]?.component?.options[sectionType]
    },
    toggleShowHeaderSettings() {
      this.showHeaderSettings = !this.showHeaderSettings
      if (!this.showHeaderSettings) this.updateActiveElement(null)
    },
    updateHeaderSelectedFlag(flag: boolean) {
      this.headerSelected = flag
    },
    updateHeaderSelected(flag: boolean) {
      if (!this.isDocumentEditable) return
      this.headerSelected = flag

      let newHeader: IElement | undefined = undefined

      this.pages = this.pages.map(page => {
        const headerAlreadyExists = page?.component?.options?.headers || undefined
        newHeader = flag ? headerAlreadyExists || createElement({ type: ELEMENTS_LOOKUP.PAGE_SECTION }) : undefined

        // When removing the header section (flag = false), also clear the
        // persisted `headerHeight` so downstream consumers (PDF page-break
        // math, webshot @page margins, viewer band sizing) fall back to
        // `DEFAULT_SECTION_OR_MARGIN_HEIGHT` (48). Without this, an empty
        // section that was just dismissed could leave a stale custom height
        // (e.g. 77 from an earlier edit) on the page, mismatching the actual
        // empty band the user now sees.
        const nextOptions = {
          ...page.component.options,
          headers: newHeader,
          ...(flag ? {} : { headerHeight: undefined }),
        }

        return {
          ...page,
          component: {
            ...page.component,
            options: nextOptions,
          },
        }
      })

      // Update store header section reference once after mapping
      this.headerSection = newHeader

      this.updateSectionActivationState()
    },
    /** Shared logic for opening header section (double-click or cogwheel). Same side-effects every time. */
    openHeaderSectionForEditing(): boolean {
      if (!this.isDocumentEditable) return false
      if (!this.headerSelected) this.updateHeaderSelected(true)
      const section = this.pages[0]?.component?.options?.[PAGE_SECTIONS.HEADERS] ?? this.headerSection
      if (!section?.id) return false
      this.headerSection = section
      ;(section as IElement).type = ELEMENTS_LOOKUP.PAGE_SECTION
      this.updateActiveElement(section as IElement)
      return true
    },
    /** Shared logic for opening footer section (double-click or cogwheel). Same side-effects every time. */
    openFooterSectionForEditing(): boolean {
      if (!this.isDocumentEditable) return false
      if (!this.footerSelected) this.updateFooterSelected(true)
      const section = this.pages[0]?.component?.options?.[PAGE_SECTIONS.FOOTERS] ?? this.footerSection
      if (!section?.id) return false
      this.footerSection = section
      ;(section as IElement).type = ELEMENTS_LOOKUP.PAGE_SECTION
      this.updateActiveElement(section as IElement)
      return true
    },
    updateFooterSelectedFlag(flag: boolean) {
      this.footerSelected = flag
    },
    updateFooterSelected(flag: boolean) {
      if (!this.isDocumentEditable) return
      this.footerSelected = flag

      let newFooter: IElement | undefined = undefined

      this.pages = this.pages.map(page => {
        const footerAlreadyExists = page?.component?.options?.footers || undefined
        newFooter = flag ? footerAlreadyExists || createElement({ type: ELEMENTS_LOOKUP.PAGE_SECTION }) : undefined

        // When removing the footer section (flag = false), also clear the
        // persisted `footerHeight` so consumers fall back to the default
        // (48). See the equivalent comment in `updateHeaderSelected`.
        const nextOptions = {
          ...page.component.options,
          footers: newFooter,
          ...(flag ? {} : { footerHeight: undefined }),
        }

        return {
          ...page,
          component: {
            ...page.component,
            options: nextOptions,
          },
        }
      })

      // Update store footer section reference once after mapping
      this.footerSection = newFooter

      // Update section activation state when footer selection changes
      this.updateSectionActivationState()
    },
    updateEditorInstance(editor: typeof Editor) {
      if (!this.isDocumentEditable) return
      this.editor = editor as typeof Editor
    },
    updateActiveElement(element: IElement | null) {
      if (!this.isDocumentEditable) return
      const prevActiveId = this.activeElementId
      if (element?.id) {
        this.activeElementId = cloneDeep(element).id
        if (element && ![ELEMENTS_LOOKUP.TEXT, ELEMENTS_LOOKUP.TABLE].includes(element.type)) this.clearEditorInstance()
        // this.createRedoStack()
      }
      if (!element) {
        this.activeElementId = null
      }
      // Update section activation state
      this.updateSectionActivationState()

      // Cleanup untouched header/footer sections that were created by a no-op
      // double-click. We never persist a section the user opened but didn't
      // edit, so the snapshot path / saved doc doesn't carry an empty section.
      //
      // Two triggers:
      //   1) The element we just deselected from was the header or footer
      //      section root → clean up that specific side if it's still
      //      untouched (Option A).
      //   2) `activeElementId` is now null → regressively check both header
      //      and footer regardless of where the user came from. Catches the
      //      case where the user opened a section, clicked through other
      //      elements without editing, and then deselected: prev-active was
      //      not the section, so trigger #1 wouldn't fire, but the empty
      //      section is still in state from the original double-click.
      if (prevActiveId && prevActiveId !== this.activeElementId) {
        const wasHeaderSectionActive = this.pages.some(p => p?.component?.options?.[PAGE_SECTIONS.HEADERS]?.id === prevActiveId)
        const wasFooterSectionActive = this.pages.some(p => p?.component?.options?.[PAGE_SECTIONS.FOOTERS]?.id === prevActiveId)
        if (wasHeaderSectionActive && this.checkPageSectionIsUntouched(PAGE_SECTIONS.HEADERS)) {
          this.updateHeaderSelected(false)
        }
        if (wasFooterSectionActive && this.checkPageSectionIsUntouched(PAGE_SECTIONS.FOOTERS)) {
          this.updateFooterSelected(false)
        }
      }
      if (this.activeElementId == null) {
        if (this.headerSelected && this.checkPageSectionIsUntouched(PAGE_SECTIONS.HEADERS)) {
          this.updateHeaderSelected(false)
        }
        if (this.footerSelected && this.checkPageSectionIsUntouched(PAGE_SECTIONS.FOOTERS)) {
          this.updateFooterSelected(false)
        }
      }
    },
    /**
     * Returns true when the given section (`headers` or `footers`) on the
     * first page is in its freshly-created state — no children, no
     * non-default background color, no background image src. Used by
     * `updateActiveElement` to decide whether to drop a section the user
     * opened via double-click but never actually edited.
     */
    checkPageSectionIsUntouched(sectionType: typeof PAGE_SECTIONS.HEADERS | typeof PAGE_SECTIONS.FOOTERS): boolean {
      const firstPage = this.pages[0]
      const section = firstPage?.component?.options?.[sectionType] as IElement | undefined
      if (!section) return true
      // Any persisted child element means the user added something.
      if (Array.isArray(section.children) && section.children.length > 0) return false
      // A background image was set via the section's properties tray.
      const src = section.component?.options?.src
      if (src != null && String(src).trim() !== '') return false
      // A non-default background color was set. The factory creates the section
      // with `#ffffff`; treat that and equivalent "no color" values as untouched.
      const bgColor = section.responsiveStyles?.large?.backgroundColor
      if (bgColor != null) {
        const normalized = String(bgColor).trim().toLowerCase()
        const isDefault =
          normalized === '' ||
          normalized === '#ffffff' ||
          normalized === '#fff' ||
          normalized === 'white' ||
          normalized === 'transparent' ||
          normalized === 'rgb(255, 255, 255)' ||
          normalized === 'rgba(255, 255, 255, 1)' ||
          normalized === 'rgba(0, 0, 0, 0)'
        if (!isDefault) return false
      }
      return true
    },
    updateSectionActivationState() {
      const activeElementId = this.activeElementId

      // Helper: does the currently active group sit inside the given section?
      // Multi-select / "Add to group" / drag-grouping ops set `activeGroup`
      // and clear `activeElementId` (because there is no single active
      // element). Without this check, a checkbox group living in the header
      // would lose the section highlight the moment the user multi-selects
      // its members, forcing them to click the section again before they
      // can keep editing — annoying for grouping flows.
      const activeGroupId = this.activeGroup?.id ?? null
      const activeGroupChildIds: string[] = activeGroupId
        ? ((this.activeGroup as any)?.children ?? []).map((c: any) => c?.elementId).filter((id: any) => typeof id === 'string')
        : []
      const anyGroupChildInSection = (sectionType: string): boolean => {
        if (activeGroupChildIds.length === 0) return false
        for (const childId of activeGroupChildIds) {
          if ((this as any).getElementSection(childId) === sectionType) return true
        }
        return false
      }

      // Update header section activation
      let headerActive = false
      // Condition 1: Header section itself is the active element (double-clicked or newly enabled)
      // Check if activeElement is a PAGE_SECTION and is a header section from any page
      if (activeElementId) {
        // First check: Is the active element a header section from any page?
        for (const page of this.pages) {
          const headerSection = page?.component?.options?.[PAGE_SECTIONS.HEADERS]
          if (headerSection && headerSection.id === activeElementId) {
            headerActive = true
            break
          }
        }
        // Second check: Is an element within the header section active?
        if (!headerActive) {
          const elementSection = (this as any).getElementSection(activeElementId)
          if (elementSection === PAGE_SECTIONS.HEADERS) {
            headerActive = true
          }
        }
      }
      // Third check: An active group whose members are inside the header
      // section keeps the header highlighted even when activeElementId is null.
      if (!headerActive && anyGroupChildInSection(PAGE_SECTIONS.HEADERS)) {
        headerActive = true
      }

      // Update footer section activation
      let footerActive = false
      // Condition 1: Footer section itself is the active element (double-clicked or newly enabled)
      // Check if activeElement is a PAGE_SECTION and is a footer section from any page
      if (activeElementId) {
        // First check: Is the active element a footer section from any page?
        for (const page of this.pages) {
          const footerSection = page?.component?.options?.[PAGE_SECTIONS.FOOTERS]
          if (footerSection && footerSection.id === activeElementId) {
            footerActive = true
            break
          }
        }
        // Second check: Is an element within the footer section active?
        if (!footerActive) {
          const elementSection = (this as any).getElementSection(activeElementId)
          if (elementSection === PAGE_SECTIONS.FOOTERS) {
            footerActive = true
          }
        }
      }
      // Third check: same group-aware rule for the footer section.
      if (!footerActive && anyGroupChildInSection(PAGE_SECTIONS.FOOTERS)) {
        footerActive = true
      }

      // Use $patch to update state properties atomically
      if (this.isHeaderSectionActive !== headerActive || this.isFooterSectionActive !== footerActive) {
        this.$patch({
          isHeaderSectionActive: headerActive,
          isFooterSectionActive: footerActive,
        })
      }
    },
    splitPage(pageId: string, index: number) {
      const pageIndex = this.pages.findIndex(x => x.id === pageId)
      if (pageIndex > -1) {
        const page = this.pages[pageIndex]
        const newPage = createElement({ type: ELEMENTS_LOOKUP.PAGE })
        if (newPage && page.children?.length) {
          const elements = page.children?.splice(index)
          if (elements?.length) {
            newPage.children = elements
            this.pages.splice(pageIndex + 1, 0, newPage)
          }
        } else if (newPage) {
          this.pages.splice(pageIndex + 1, 0, newPage)
        }

        // Only clone header/footer onto the freshly-created split page. Existing pages keep
        // their section objects untouched (no Vue prop reference churn → no editor re-sync).
        if (newPage) {
          this.syncHeaderFooterToNewPage(newPage.id)
        }
      }
    },
    /**
     * Applies the store's header/footer sections to every page.
     * Use after setPages (e.g. PDF merge) or addPage so new pages stay in sync.
     */
    syncHeaderFooterToAllPages() {
      // Skip the whole fanout when neither the header nor the footer actually carries
      // any content — there is nothing to replicate across pages, so burning a
      // `pages.forEach` (and a checkbox-group replica walk) on empty sections is wasted
      // work and also shouldn't flash a loader at the user.
      const headerHasChildren =
        this.headerSelected && Array.isArray(this.headerSection?.children) && (this.headerSection!.children as any[]).length > 0
      const footerHasChildren =
        this.footerSelected && Array.isArray(this.footerSection?.children) && (this.footerSection!.children as any[]).length > 0
      if (!headerHasChildren && !footerHasChildren) {
        return
      }

      const doSync = () => {
        // Rebuild the clone-id map from current page state BEFORE the clone loop runs.
        // The rebuild now also records source-page identity entries (id → itself for
        // whichever page currently owns the section). `cloneSectionWithStableFillableIds`
        // reads those via `existingCloneId` and keeps the source page's ids intact instead
        // of regenerating them. Without this, on the very first add-page after a group is
        // created the source page's ids get rewritten and the group's `children[].elementId`
        // references go dead — both pages render ungrouped and the replication walk can't
        // recover (no candidate group survives the rewrite).
        const cloneMap = (this.headerFooterCloneIdMap = buildHeaderFooterCloneIdMapFromPages(this.pages))

        if (headerHasChildren) {
          this.pages.forEach(page => {
            page.component.options[PAGE_SECTIONS.HEADERS] = cloneSectionWithStableFillableIds(this.headerSection!, page.id, cloneMap)
          })
          this.headerSection = this.pages[0]?.component?.options?.[PAGE_SECTIONS.HEADERS]
        }

        if (footerHasChildren) {
          this.pages.forEach(page => {
            page.component.options[PAGE_SECTIONS.FOOTERS] = cloneSectionWithStableFillableIds(this.footerSection!, page.id, cloneMap)
          })
          this.footerSection = this.pages[0]?.component?.options?.[PAGE_SECTIONS.FOOTERS]
        }

        // Rebuild the clone-id map AFTER the clone loop so it's re-keyed by the
        // current canonical-source page (the first page that now holds the section).
        // Without this rebuild, the map stays keyed by the OLD source page's element
        // ids — which leaks into any subsequent `cloneSectionWithStableFillableIds`
        // call (e.g. user edits the header right after adding a page) and forces
        // those re-clones into the regenerate branch instead of the identity branch,
        // overwriting per-page ids that downstream consumers (active element,
        // group children, ProseMirror anchors, page-level fillable markers) hold
        // onto. Was previously a side-effect of `replicateAllHeaderFooterCheckboxGroupsAcrossPages`.
        this.headerFooterCloneIdMap = buildHeaderFooterCloneIdMapFromPages(this.pages)
      }

      // For small documents the fanout completes in a single frame — showing a blocking
      // overlay for that would be noise. Only show the loader when the doc is large
      // enough for the user to actually perceive the blocking work (> 30 pages).
      const needsLoader = this.pages.length > 30
      if (!needsLoader) {
        doSync()
        return
      }

      // Flip on the loader flag BEFORE the heavy pages.forEach work starts. The double-RAF
      // yield gives the browser a chance to paint the overlay before Vue gets blocked by
      // the synchronous clone + replicate loop — without it, the loader would only become
      // visible AFTER the work was done (defeating the point). Clear the flag inside a
      // trailing RAF so Vue has a frame to flush reactive updates before the overlay
      // unmounts.
      this.isApplyingHeaderFooterChanges = true
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          try {
            doSync()
          } finally {
            requestAnimationFrame(() => {
              this.isApplyingHeaderFooterChanges = false
            })
          }
        })
      )
    },

    updateSectionChangeToEveryPage(hostPageId = '', sectionType: string) {
      if (!hostPageId) return
      const hostPageIndex = this.pages.findIndex(x => x.id === hostPageId)
      if (hostPageIndex <= -1) return
      const hostPage = this.pages[hostPageIndex]
      const section = hostPage.component.options[sectionType]
      if (!section) return

      // The actual per-page clone + sync work. Separated out so we can either run it
      // inline (small docs) or wrap it in a loader + RAF yields (large docs) without
      // duplicating the loop.
      const runSync = () => {
        this.pages.forEach(page => {
          if (page.id !== hostPageId) {
            const clone = cloneSectionWithStableFillableIds(
              section,
              page.id,
              (this.headerFooterCloneIdMap = this.headerFooterCloneIdMap ?? {})
            )
            syncSectionMediaDimensions(clone, section)
            page.component.options[sectionType] = clone
          }
        })
      }

      // For ≤ 20 pages the fanout completes in a single frame — a loader would flash
      // at the user and feel worse than no loader. Only wrap the heavy branch.
      if (this.pages.length <= 20) {
        runSync()
        return
      }

      // Flip the loader flag ON, yield two animation frames so the overlay actually paints
      // before the blocking pages.forEach runs, then do the work. Trailing rAF clears the
      // flag after Vue has flushed reactive re-renders.
      this.isApplyingHeaderFooterChanges = true
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          try {
            runSync()
          } finally {
            requestAnimationFrame(() => {
              this.isApplyingHeaderFooterChanges = false
            })
          }
        })
      )
    },

    addElement(
      element: IElement,
      pageId: string,
      rowIndex = -1, // Defaults to -1 for top-level insertion
      colIndex = -1, // Defaults to -1 for adding directly to row, 0+ for column
      rowColIndex = -1 // Index of children inside column, 0+ to specify position
    ) {
      // console.log('add element called!!!')
      if (!this.isDocumentEditable) return

      const pageIndex = this.pages.findIndex(x => x.id === pageId)
      if (pageIndex > -1) {
        const page = this.pages[pageIndex]
        if (colIndex > -1) {
          const row = page.children[rowIndex]
          if (row?.type === ELEMENTS_LOOKUP.ROW) {
            const newColumnElement = createElement({
              type: ELEMENTS_LOOKUP.COLUMN,
            })
            if (rowColIndex > -1) {
              this.pages[pageIndex]?.children[rowIndex]?.children[colIndex]?.children?.splice(rowColIndex, 0, element as IElement)
            } else {
              newColumnElement?.children.push(element)
              this.pages[pageIndex]?.children[rowIndex]?.children?.splice(colIndex, 0, newColumnElement as IElement)
              this.pages[pageIndex]?.children[rowIndex]?.children?.forEach((child: IElement) => {
                child.responsiveStyles.large.width = ''
              })
            }
          } else {
            const columnForExistingElement = createElement({
              type: ELEMENTS_LOOKUP.COLUMN,
            })
            const columnForNewElement = createElement({
              type: ELEMENTS_LOOKUP.COLUMN,
            })
            const newRowElement = createElement({ type: ELEMENTS_LOOKUP.ROW })
            columnForExistingElement?.children.push(row)
            columnForNewElement?.children.push(element)
            newRowElement?.children.push(columnForExistingElement as IElement)
            newRowElement?.children.splice(colIndex, 0, columnForNewElement as IElement)
            page.children.splice(rowIndex, 1, newRowElement as IElement)
          }
        } else {
          page.children.splice(rowIndex, 0, element)
          if (element.type === ELEMENTS_LOOKUP.PAGE_BREAK) {
            this.splitPage(pageId, rowIndex)
          }
        }
        this.updateActiveElement(element)
        this.createRedoStack()
      }
    },
    addElementsFromContentLibrary(
      {
        pages,
        groups,
        customValueLinkage,
      }: {
        pages: IElement[]
        groups: GroupFields<GroupFieldsOptions>[]
        customValueLinkage: Record<string, ICustomFieldsLink>
      },
      pageId: string,
      rowIndex = -1, // Defaults to -1 for top-level insertion
      colIndex = -1 // Defaults to -1 for adding directly to row, 0+ for column
    ) {
      if (pages.length === 0) return
      const firstPage = pages[0]
      if (!this.isDocumentEditable) return
      const pageIndex = this.pages.findIndex(x => x.id === pageId)
      if (pageIndex > -1) {
        const page = this.pages[pageIndex]
        if (colIndex === -1) {
          // iterate through  the children in firstPage and add them to the page and change the id
          const { data: clonedFirstPage, idMap } = cloneWithNewIds(firstPage)
          if (clonedFirstPage) {
            if (clonedFirstPage.children && Array.isArray(clonedFirstPage.children) && clonedFirstPage.children.length > 0) {
              clonedFirstPage.children.reverse().forEach(newElement => {
                page.children.splice(rowIndex, 0, newElement)
              })
              this.syncRecipient()
              if (groups && groups.length > 0) {
                // iterate through the groups and update the id of the element
                const updatedGroups = cloneGroupWithNewIds(groups, idMap)
                this.groups = [...this.groups, ...updatedGroups]
              }

              if (customValueLinkage) {
                const reMappedCVLinkage = remapKeysForCVLinkage(customValueLinkage, idMap)
                if (reMappedCVLinkage) {
                  this.addNewElementsToCustomValueLinkage(reMappedCVLinkage)
                }
              }
              this.createRedoStack(true)
            }
          }
        }
      }
    },
    addElementsFromContentLibraryToPage(
      {
        pages,
        groups,
        customValueLinkage,
      }: {
        pages: IElement[]
        groups: GroupFields<GroupFieldsOptions>[]
        customValueLinkage: Record<string, ICustomFieldsLink>
      },
      index: number
    ) {
      if (pages.length === 0) return
      const firstPage = pages[0]
      if (!this.isDocumentEditable) return
      if (firstPage) {
        const { data: clonedFirstPage, idMap } = cloneWithNewIds(firstPage)
        this.pages.splice(index + 1, 0, clonedFirstPage)
        this.syncRecipient()
        if (groups && groups.length > 0) {
          const updatedGroups = cloneGroupWithNewIds(groups, idMap)
          this.groups = [...this.groups, ...updatedGroups]
        }
        if (customValueLinkage) {
          const reMappedCVLinkage = remapKeysForCVLinkage(customValueLinkage, idMap)
          if (reMappedCVLinkage) {
            this.addNewElementsToCustomValueLinkage(reMappedCVLinkage)
          }
        }
        this.createRedoStack(true)
      }
    },
    addElementToSection(
      element: IElement,
      pageId: string,
      rowIndex = -1, // Defaults to -1 for top-level insertion
      colIndex = -1, // Defaults to -1 for adding directly to row, 0+ for column
      rowColIndex = -1, // Index of children inside column, 0+ to specify position
      sectionType = PAGE_SECTIONS.HEADERS
    ) {
      if (!this.isDocumentEditable) return

      // Defense-in-depth: checkbox is disallowed inside header/footer sections
      // because the per-page checkbox-grouping replication produces too many
      // edge cases (id-mapping skew, group membership drift, replica footprint
      // mismatches). The PageSection drop handler in the UI already rejects
      // drag-drops, but we re-enforce here so any other caller (content library,
      // programmatic insertion, undo/redo bringing a stale checkbox back into
      // a section, etc.) is also blocked.
      if (element?.type === ELEMENTS_LOOKUP.CHECKBOX && (sectionType === PAGE_SECTIONS.HEADERS || sectionType === PAGE_SECTIONS.FOOTERS)) {
        return
      }

      // Ensure the element has a unique ID and is a deep copy to avoid reference issues
      const elementToAdd = cloneDeep(element)
      if (!elementToAdd.id) {
        elementToAdd.id = generateElementId()
      }

      // Always operate on the CANONICAL source page (the first page that
      // currently holds this section). `cloneIdMap` is one-directional —
      // its keys are canonical-source element ids, values are
      // `{ targetPageId → cloneId }`. Mutating a non-canonical page (e.g.
      // the user dropped on page 2's header in a multi-page doc) and then
      // calling `updateSectionChangeToEveryPage(page2.id, …)` makes
      // `cloneSectionWithStableFillableIds` mint fresh UUIDs for every
      // block on every other page (page 2's element ids aren't keys in
      // `cloneIdMap`, so the helper falls into the regenerate branch).
      // That cascade rewrites page 1's element ids mid-drop, orphans
      // `actionBarStore.lastActiveDragElement.id`, and makes the visible
      // split-column "do nothing" — the move-existing branch in
      // `PageSection.onDropElement` runs but the propagate immediately
      // overwrites it from a host whose ids `cloneIdMap` doesn't know.
      // Redirecting to canonical here keeps the source-of-truth stable
      // and lets the existing replication propagate the new structure to
      // every other page with the right per-page ids.
      const canonicalSectionPageId = this.pages.find((p: any) => p?.component?.options?.[sectionType])?.id
      if (canonicalSectionPageId && canonicalSectionPageId !== pageId) {
        pageId = canonicalSectionPageId
      }

      const pageIndex = this.pages.findIndex(x => x.id === pageId)
      if (pageIndex > -1) {
        const page = this.pages[pageIndex]
        if (!page.component.options[sectionType]) {
          page.component.options[sectionType] = {}
          page.component.options[sectionType].children = []
        }
        if (!page.component.options[sectionType].children) {
          page.component.options[sectionType].children = []
        }
        const sectionChildren = page.component.options[sectionType].children

        if (colIndex > -1) {
          let effectiveRowIndex = rowIndex
          let row = sectionChildren[effectiveRowIndex]
          if (!row && sectionChildren.length > 0) {
            effectiveRowIndex = Math.min(Math.max(0, rowIndex), sectionChildren.length - 1)
            row = sectionChildren[effectiveRowIndex]
          }
          if (!row) {
            return
          }
          if (row?.type === ELEMENTS_LOOKUP.ROW) {
            const newColumnElement = createElement({
              type: ELEMENTS_LOOKUP.COLUMN,
            })
            if (rowColIndex > -1) {
              this.pages[pageIndex]?.component.options[sectionType].children[effectiveRowIndex]?.children[colIndex]?.children?.splice(
                rowColIndex,
                0,
                elementToAdd as IElement
              )
            } else {
              newColumnElement?.children.push(elementToAdd)
              this.pages[pageIndex].component.options[sectionType]?.children[effectiveRowIndex]?.children?.splice(
                colIndex,
                0,
                newColumnElement as IElement
              )
              this.pages[pageIndex].component.options[sectionType]?.children[effectiveRowIndex]?.children?.forEach((child: IElement) => {
                child.responsiveStyles.large.width = ''
              })
            }
          } else {
            const columnForExistingElement = createElement({
              type: ELEMENTS_LOOKUP.COLUMN,
            })
            const columnForNewElement = createElement({
              type: ELEMENTS_LOOKUP.COLUMN,
            })
            const newRowElement = createElement({ type: ELEMENTS_LOOKUP.ROW })
            columnForExistingElement?.children.push(row)
            columnForNewElement?.children.push(elementToAdd)
            newRowElement?.children.push(columnForExistingElement as IElement)
            newRowElement?.children.splice(colIndex, 0, columnForNewElement as IElement)
            page.component.options[sectionType].children.splice(effectiveRowIndex, 1, newRowElement as IElement)
          }
        } else {
          page.component.options[sectionType].children.splice(rowIndex, 0, elementToAdd)
        }

        //Replicate the same change to every page
        this.updateSectionChangeToEveryPage(pageId, sectionType)
        // Refresh canonical reference so a subsequent `syncHeaderFooterToAllPages` (e.g. user
        // clicks "+ Add page" right after adding a fillable) clones from the up-to-date section
        // rather than a stale snapshot captured before the fillable was added.
        if (sectionType === PAGE_SECTIONS.HEADERS) {
          this.headerSection = this.pages[0]?.component?.options?.[PAGE_SECTIONS.HEADERS]
        } else if (sectionType === PAGE_SECTIONS.FOOTERS) {
          this.footerSection = this.pages[0]?.component?.options?.[PAGE_SECTIONS.FOOTERS]
        }
        this.updateActiveElement(elementToAdd)
        this.createRedoStack()
      }
    },
    moveElementInArray<T>(array: T[], fromIndex: number, toIndex: number): T[] {
      if (fromIndex < 0 || fromIndex >= array.length || toIndex < 0 || toIndex >= array.length || fromIndex === toIndex) {
        return array
      }

      const newArray: T[] = [...array]
      const [movedElement] = newArray.splice(fromIndex, 1)
      if (movedElement === undefined) return array
      newArray.splice(toIndex, 0, movedElement)

      return newArray
    },
    moveElement(element: IElement, pageId: string, newIndex: number, oldIndex: number) {
      if (!this.isDocumentEditable) return
      const pageIndex = this.pages.findIndex(x => x.id === pageId)
      if (pageIndex > -1) {
        const elements = this.pages[pageIndex].children
        const elementsWithoutDraggable = elements.filter(child => !child.component.isDraggable)
        const draggableElements = elements.filter(child => child.component.isDraggable)
        this.pages[pageIndex].children = [
          ...this.moveElementInArray(cloneDeep(elementsWithoutDraggable), oldIndex, newIndex),
          ...draggableElements,
        ]
      }
      this.updateActiveElement(element)
      if (![ELEMENTS_LOOKUP.TEXT, ELEMENTS_LOOKUP.TABLE].includes(element.type)) {
        this.clearEditorInstance()
      }
      this.createRedoStack()
    },
    moveElementInSection(element: IElement, pageId: string, newIndex: number, oldIndex: number, sectionType = PAGE_SECTIONS.HEADERS) {
      if (!this.isDocumentEditable) return
      // Always operate on the canonical source page (first page with the
      // section). See `addElementToSection` for the full rationale —
      // mutating + propagating from a non-canonical host invalidates
      // `cloneIdMap` and rewrites every other page's element ids.
      const canonicalSectionPageId = this.pages.find((p: any) => p?.component?.options?.[sectionType])?.id
      if (canonicalSectionPageId && canonicalSectionPageId !== pageId) {
        pageId = canonicalSectionPageId
      }
      const pageIndex = this.pages.findIndex(x => x.id === pageId)
      if (pageIndex > -1) {
        if (!this.pages[pageIndex].component.options[sectionType]) return
        const elements = this.pages[pageIndex].component.options[sectionType]?.children
        if (!elements?.length) return
        const elementsWithoutDraggable = elements.filter(child => child && child.component && !child.component.isDraggable)
        const draggableElements = elements.filter(child => child && child.component && child.component.isDraggable)
        this.pages[pageIndex].component.options[sectionType].children = [
          ...this.moveElementInArray(cloneDeep(elementsWithoutDraggable), oldIndex, newIndex),
          ...draggableElements,
        ]

        this.updateSectionChangeToEveryPage(pageId, sectionType)
        // Refresh canonical reference — see `addElementToSection` for rationale. Without this,
        // a stale `headerSection`/`footerSection` snapshot can leak into a subsequent
        // `syncHeaderFooterToAllPages` and clone the wrong content onto a freshly-added page.
        if (sectionType === PAGE_SECTIONS.HEADERS) {
          this.headerSection = this.pages[0]?.component?.options?.[PAGE_SECTIONS.HEADERS]
        } else if (sectionType === PAGE_SECTIONS.FOOTERS) {
          this.footerSection = this.pages[0]?.component?.options?.[PAGE_SECTIONS.FOOTERS]
        }
      }
      this.updateActiveElement(element)
      if (![ELEMENTS_LOOKUP.TEXT, ELEMENTS_LOOKUP.TABLE].includes(element.type)) {
        this.clearEditorInstance()
      }
      this.createRedoStack()
    },
    addPage(index = 0) {
      if (!this.isDocumentEditable) return
      const pageElement = createElement({ type: ELEMENTS_LOOKUP.PAGE })

      if (pageElement) {
        const insertPosition = index <= 0 ? 0 : index + 1
        this.pages.splice(insertPosition, 0, pageElement)
        // Only clone header/footer onto the NEW page. Don't touch existing pages — that's
        // what `syncHeaderFooterToAllPages` does, and it's the wrong tool here because it
        // reassigns `page.component.options.headers = clone` on every existing page, which
        // swaps the element prop reference Vue is rendering and forces every existing
        // RichTextEditor to re-sync via the `updateEditors` watch. That re-sync races
        // against the new page's editor mount and visibly empties text blocks. The narrow
        // sync below leaves existing pages alone (so existing editors are untouched) and
        // only mints fresh per-page ids for the new page via `cloneSectionWithStableFillableIds`.
        this.syncHeaderFooterToNewPage(pageElement.id)
      }

      this.clearEditorInstance()
      this.createRedoStack()
    },
    /**
     * Clone the canonical header/footer onto a SINGLE freshly-added page (typically used
     * by `addPage`, `splitPage`, `duplicatePage`). Existing pages are left untouched —
     * their `component.options.headers` / `footers` objects retain identity, so Vue does
     * not see prop reference changes and does not force a re-render of their tiptap
     * editors / fillable components. The new page's per-page element ids are minted via
     * `cloneSectionWithStableFillableIds` and registered in `headerFooterCloneIdMap` so
     * subsequent value/style mirror updates land on it correctly.
     */
    syncHeaderFooterToNewPage(newPageId: string) {
      if (!this.isDocumentEditable) return
      const newPage = this.pages.find(p => p.id === newPageId)
      if (!newPage) return
      // Resolve canonical-source pages — first page (other than the new one) that actually
      // has the section with content.
      const sourceHeaderPage = this.headerSelected
        ? this.pages.find(p => p.id !== newPageId && p.component?.options?.[PAGE_SECTIONS.HEADERS]?.children?.length)
        : null
      const sourceFooterPage = this.footerSelected
        ? this.pages.find(p => p.id !== newPageId && p.component?.options?.[PAGE_SECTIONS.FOOTERS]?.children?.length)
        : null

      // Use the existing clone-id map; cloneSectionWithStableFillableIds will add entries
      // for the new page's targetPageId (no rebuild — rebuilds re-key by canonical source
      // and would invalidate downstream consumers that hold per-page ids).
      const cloneMap = this.headerFooterCloneIdMap ?? (this.headerFooterCloneIdMap = {})

      if (sourceHeaderPage) {
        const sourceHeader = sourceHeaderPage.component.options[PAGE_SECTIONS.HEADERS] as IElement
        newPage.component.options[PAGE_SECTIONS.HEADERS] = cloneSectionWithStableFillableIds(sourceHeader, newPageId, cloneMap)
        // Mirror the source page's persisted band height so the new page's `.page-header`
        // band is the same size — without this it falls back to `DEFAULT_SECTION_OR_MARGIN_HEIGHT`
        // which can desync from the rest of the doc.
        const srcHeaderHeight = (sourceHeaderPage.component?.options as any)?.headerHeight
        if (srcHeaderHeight !== undefined) {
          ;(newPage.component.options as any).headerHeight = srcHeaderHeight
        }
      }

      if (sourceFooterPage) {
        const sourceFooter = sourceFooterPage.component.options[PAGE_SECTIONS.FOOTERS] as IElement
        newPage.component.options[PAGE_SECTIONS.FOOTERS] = cloneSectionWithStableFillableIds(sourceFooter, newPageId, cloneMap)
        const srcFooterHeight = (sourceFooterPage.component?.options as any)?.footerHeight
        if (srcFooterHeight !== undefined) {
          ;(newPage.component.options as any).footerHeight = srcFooterHeight
        }
      }
    },
    updateElementOption(params: IUpdateElementOption) {
      if (!this.isDocumentEditable) return

      const { id, value, key, type } = params
      if (type === ELEMENTS_LOOKUP.PAGE) {
        const pageIndex = this.pages.findIndex(x => x.id === id)
        if (pageIndex > -1) {
          this.pages[pageIndex].component.options[key] = value
        }
        this.createRedoStack()
        return
      }

      const updateNestedElement = (elements: any[]) => {
        for (let index = 0; index < elements.length; index++) {
          const element = elements[index]
          // Only update if the ID matches exactly and the element has component.options
          if (element?.id === id && element?.component?.options) {
            element.component.options[key] = value
            return true
          }
          if (element?.children?.length) {
            if (updateNestedElement(element.children)) return true
          }
        }
        return false
      }

      // Iterate over all pages and their nested children to find and update the element
      for (let i = 0; i < this.pages.length; i++) {
        if (updateNestedElement(this.pages[i].children)) {
          /**
           * Apply debounce in case text block changes were made, this is to make sure a redo stack is not created
           * for every character change but only for changes with some delay
           */
          if (type === ELEMENTS_LOOKUP.TEXT) {
            if (shouldCreateNewEditorGroup) {
              this.createRedoStack()
              shouldCreateNewEditorGroup = false
            }
            debouncedCreateRedoStack.call(this)
          } else {
            this.createRedoStack()
          }
          if (shouldScheduleFloatingLayoutAfterOption(key)) {
            schedulePageFloatingLayoutSync(this, this.pages[i].id)
          }
        }

        if (this.pages[i]?.component?.options?.headers?.children) {
          if (updateNestedElement(this.pages[i].component.options.headers.children)) {
            // Simple model: whichever page is being edited, mirror that value update to the same
            // logical element on every other page using `headerFooterCloneIdMap`. The mirror walks
            // each page's section and mutates the matching element's option in place — no section
            // re-clone, no Vue prop reference churn, no risk of orphaning fillable ids in
            // `cloneIdMap`. This works for TEXT (rich-text blocks via tiptap), fillables, and any
            // other element registered in the clone map. The mirror also bumps `updateEditors`
            // internally so tiptap watchers on other pages re-sync via `setContent`.
            this.mirrorSectionFillableOptionAcrossPages(id, this.pages[i].id, PAGE_SECTIONS.HEADERS, key, value)
            if (type === ELEMENTS_LOOKUP.TEXT) {
              // Debounce the redo-stack push so a stack entry isn't created per keystroke.
              if (shouldCreateNewEditorGroup) {
                this.createRedoStack()
                shouldCreateNewEditorGroup = false
              }
              debouncedCreateRedoStack.call(this)
            } else {
              this.createRedoStack()
            }
            if (shouldScheduleFloatingLayoutAfterOption(key)) {
              scheduleAllPagesFloatingLayoutSync(this)
            }
            return
          }
        }

        if (this.pages[i]?.component?.options?.footers?.children) {
          if (updateNestedElement(this.pages[i].component.options.footers.children)) {
            // Same simple model as headers — see the comment in the headers branch above.
            this.mirrorSectionFillableOptionAcrossPages(id, this.pages[i].id, PAGE_SECTIONS.FOOTERS, key, value)
            if (type === ELEMENTS_LOOKUP.TEXT) {
              if (shouldCreateNewEditorGroup) {
                this.createRedoStack()
                shouldCreateNewEditorGroup = false
              }
              debouncedCreateRedoStack.call(this)
            } else {
              this.createRedoStack()
            }
            if (shouldScheduleFloatingLayoutAfterOption(key)) {
              scheduleAllPagesFloatingLayoutSync(this)
            }
            return
          }
        }
      }
    },
    mirrorSectionFillableOptionAcrossPages(elementId: string, hostPageId: string, sectionType: string, key: string, value: any) {
      if (!this.isDocumentEditable) return
      const cloneMap = this.headerFooterCloneIdMap ?? {}
      const targets = listHeaderFooterReplicaTargets(elementId, hostPageId, this.pages as IPage[], cloneMap)
      // Walk every page's section, mutate the matching element's option in place — do NOT replace
      // the section object. Reassigning `page.component.options[sectionType] = clone` on every
      // keystroke (what `updateSectionChangeToEveryPage` does) swaps the element prop reference
      // Vue is rendering and visibly drops the keystroke for any focused textarea/input.
      const updateInTree = (children: any[], targetId: string): boolean => {
        if (!children?.length) return false
        for (const child of children) {
          if (!child) continue
          if (child.id === targetId && child.component?.options) {
            child.component.options[key] = value
            return true
          }
          if (Array.isArray(child.children) && child.children.length) {
            if (updateInTree(child.children, targetId)) return true
          }
        }
        return false
      }
      for (const t of targets) {
        const page = this.pages.find(p => p.id === t.pageId)
        const section = page?.component?.options?.[sectionType]
        if (!section?.children) continue
        updateInTree(section.children, t.elementId)
      }
      // No `updateEditors` bump needed — RichTextEditor / RichTextTableContent each watch
      // `props.element?.component?.options?.text` directly (set up in their respective `setup()`),
      // so tiptap's `setContent` runs naturally when the prop's text value changes via the mutation
      // above. Vue's reactivity already tracks the per-page mutation; we don't need a separate
      // signal counter to fan out the change.
      if (shouldScheduleFloatingLayoutAfterOption(key)) {
        scheduleAllPagesFloatingLayoutSync(this)
      }
    },
    /**
     * Mirror a `responsiveStyles.large[key]` update (e.g. position, dimensions, scale, padding)
     * for a header/footer element to every page that has the same logical element registered
     * in `headerFooterCloneIdMap`. Mutates each page's matching element in place — does NOT
     * replace the section object. Same idea as `mirrorSectionFillableOptionAcrossPages` but
     * for styles rather than component options. Used by `updateElementStyle` to propagate
     * fillable drag/resize/scale changes across pages without going through
     * `updateSectionChangeToEveryPage` (whose section re-clone regenerates UUIDs for fillables
     * on other pages when the host is non-canonical, visibly breaking the "fillable on page 1
     * snaps to top-right when dragged on page 2" case).
     */
    mirrorSectionElementStyleAcrossPages(elementId: string, hostPageId: string, sectionType: string, key: string, value: any) {
      if (!this.isDocumentEditable) return
      const cloneMap = this.headerFooterCloneIdMap ?? {}
      const targets = listHeaderFooterReplicaTargets(elementId, hostPageId, this.pages as IPage[], cloneMap)
      const updateInTree = (children: any[], targetId: string): boolean => {
        if (!children?.length) return false
        for (const child of children) {
          if (!child) continue
          if (child.id === targetId) {
            if (!child.responsiveStyles) child.responsiveStyles = { large: {} } as any
            if (!child.responsiveStyles.large) child.responsiveStyles.large = {} as any
            ;(child.responsiveStyles.large as any)[key] = cloneDeep(value)
            return true
          }
          if (Array.isArray(child.children) && child.children.length) {
            if (updateInTree(child.children, targetId)) return true
          }
        }
        return false
      }
      for (const t of targets) {
        const page = this.pages.find(p => p.id === t.pageId)
        const section = page?.component?.options?.[sectionType]
        if (!section?.children) continue
        updateInTree(section.children, t.elementId)
      }
    },
    propagateElementOptionToAllPages(elementId: string, key: string, value: any, sectionType: string) {
      if (!elementId) return // Safety check: ensure elementId is provided

      // Helper function to update element option in nested structure
      // This function updates ONLY elements with the matching ID
      const updateNestedElementOption = (elements: any[]): void => {
        for (const element of elements) {
          // Only update if the ID matches exactly
          if (element?.id === elementId && element.component?.options) {
            element.component.options[key] = value
          }
          // Continue searching in nested children
          if (element?.children?.length) {
            updateNestedElementOption(element.children)
          }
        }
      }

      // Update the option on all pages that have this section
      for (const page of this.pages) {
        const section = page.component?.options?.[sectionType]
        if (section?.children) {
          updateNestedElementOption(section.children)
        }
      }
      if (shouldScheduleFloatingLayoutAfterOption(key)) {
        scheduleAllPagesFloatingLayoutSync(this)
      }
    },
    flushPendingSectionPropagations(elementId?: string) {
      // `updateSectionChangeToEveryPage` already toggles `updateEditors` at the end of each sync,
      // so every non-host editor runs its content-sync watcher. Do NOT toggle here in addition —
      // pairs of synchronous flips cancel out in Vue's reactive flush and the watcher ends up
      // seeing the original value, silently skipping `editor.commands.setContent`. That's the
      // regression that left text blocks stale on non-host pages after any flush.
      if (elementId) {
        const keysToFlush: string[] = []
        pendingSectionPropagations.forEach((value, key) => {
          if (value.elementId === elementId) {
            keysToFlush.push(key)
            if (value.debouncedFn) {
              value.debouncedFn.cancel()
            }
            this.updateSectionChangeToEveryPage(value.hostPageId, value.sectionType)
          }
        })
        keysToFlush.forEach(key => pendingSectionPropagations.delete(key))
        if (keysToFlush.length > 0) {
          scheduleAllPagesFloatingLayoutSync(this)
        }
      } else {
        pendingSectionPropagations.forEach(value => {
          if (value.debouncedFn) {
            value.debouncedFn.cancel()
          }
          this.updateSectionChangeToEveryPage(value.hostPageId, value.sectionType)
        })
        pendingSectionPropagations.clear()
        scheduleAllPagesFloatingLayoutSync(this)
      }
    },
    updateElementStyle(params: IUpdateElementOption) {
      if (!this.isDocumentEditable) return
      const { id, value, key, type } = params
      if (type === ELEMENTS_LOOKUP.PAGE) {
        const pageIndex = this.pages.findIndex(x => x.id === id)
        if (pageIndex > -1) {
          this.pages[pageIndex].responsiveStyles.large[key] = cloneDeep(value)
        }
        this.createRedoStack()
        return
      }
      const updateStyle = (children: IElement[]) => {
        for (let i = 0; i < children.length; i++) {
          if (children[i]?.id === id) {
            children[i].responsiveStyles.large[key] = cloneDeep(value)
            return true
          }
          if (children[i]?.children?.length) {
            const found = updateStyle(children[i].children)
            if (found) return true
          }
        }
        return false
      }

      // Iterate over all pages and their nested children to find and update the element's style
      for (const page of this.pages) {
        if (updateStyle(page?.children)) {
          if (shouldScheduleFloatingLayoutAfterStyle(key, id, this.pages)) {
            schedulePageFloatingLayoutSync(this, page.id)
          }
          this.createRedoStack()
          return
        }

        if (page.component.options.headers && page.component.options.headers.children) {
          if (updateStyle(page.component.options.headers.children)) {
            // Mirror this style update across every page using `headerFooterCloneIdMap`. Same
            // simple model as the value-update path: walk every page's section, mutate the
            // matching element's `responsiveStyles.large[key]` in place. Avoids the section
            // re-clone path that regenerates fillable UUIDs on the canonical page when the
            // host is a non-canonical page (which made a fillable on page 1 snap to top-right
            // when dragged on page 2).
            this.mirrorSectionElementStyleAcrossPages(id, page.id, PAGE_SECTIONS.HEADERS, key, value)
            if (shouldScheduleFloatingLayoutAfterStyle(key, id, this.pages)) {
              scheduleAllPagesFloatingLayoutSync(this)
            }
            this.createRedoStack()
            this.validateSectionHeights()
            return
          }
        }

        if (page.component.options.footers && page.component.options.footers.children) {
          if (updateStyle(page.component.options.footers.children)) {
            this.mirrorSectionElementStyleAcrossPages(id, page.id, PAGE_SECTIONS.FOOTERS, key, value)
            if (shouldScheduleFloatingLayoutAfterStyle(key, id, this.pages)) {
              scheduleAllPagesFloatingLayoutSync(this)
            }
            this.createRedoStack()
            this.validateSectionHeights()
            return
          }
        }
      }
    },
    updateSectionElementStyle(params: IUpdateSectionElementOption) {
      if (!this.isDocumentEditable) return
      const { id, value, key, section: sectionType } = params

      // First try to find the section in any page
      for (const page of this.pages) {
        const section = page.component.options[sectionType]
        if (section?.id === id) {
          // Found the section, update its style
          section.responsiveStyles.large[key] = cloneDeep(value)

          // If this is a header or footer, propagate the change to all pages
          if (sectionType === PAGE_SECTIONS.HEADERS || sectionType === PAGE_SECTIONS.FOOTERS) {
            this.updateSectionChangeToEveryPage(page.id, sectionType)
            this.validateSectionHeights()
            if (shouldScheduleFloatingLayoutAfterStyle(key, id, this.pages)) {
              scheduleAllPagesFloatingLayoutSync(this)
            }
          }

          this.createRedoStack()
          return
        }
      }

      // If not found at section level, search in nested elements
      const updateStyle = (children: IElement[]) => {
        for (let i = 0; i < children.length; i++) {
          if (children[i]?.id === id) {
            children[i].responsiveStyles.large[key] = cloneDeep(value)
            return true
          }
          if (children[i]?.children?.length) {
            const found = updateStyle(children[i].children)
            if (found) return true
          }
        }
        return false
      }

      // Iterate over all pages and their nested children to find and update the element's style
      for (const page of this.pages) {
        if (page.component.options[sectionType]?.children) {
          if (updateStyle(page.component.options[sectionType].children)) {
            if (sectionType === PAGE_SECTIONS.HEADERS || sectionType === PAGE_SECTIONS.FOOTERS) {
              this.updateSectionChangeToEveryPage(page.id, sectionType)
              this.validateSectionHeights()
              if (shouldScheduleFloatingLayoutAfterStyle(key, id, this.pages)) {
                scheduleAllPagesFloatingLayoutSync(this)
              }
            }
            this.createRedoStack()
            return
          }
        }
      }
    },
    updateSectionElementOption(params: IUpdateSectionElementOption) {
      if (!this.isDocumentEditable) return
      const { id, value, key, section: sectionType } = params

      // First try to find the section in any page
      for (const page of this.pages) {
        const section = page.component.options[sectionType]
        if (section?.id === id) {
          // Found the section, update its option
          section[key] = value

          // Update store values if this is the header or footer section
          if (sectionType === PAGE_SECTIONS.HEADERS) {
            if (this.headerSection?.id === id) this.headerSection[key] = value
          } else if (sectionType === PAGE_SECTIONS.FOOTERS) {
            if (this.footerSection?.id === id) this.footerSection[key] = value
          }

          // If this is a header or footer, propagate the change to all pages
          if (sectionType === PAGE_SECTIONS.HEADERS || sectionType === PAGE_SECTIONS.FOOTERS) {
            const firstPage = this.pages[0]
            if (firstPage) {
              // If we updated the section on a different page, also update it on the first page
              // (which should be the source of truth for header/footer sections)
              if (page.id !== firstPage.id) {
                const firstPageSection = firstPage.component.options[sectionType]
                if (firstPageSection?.id === id) {
                  // Update the first page's section as well
                  firstPageSection[key] = value
                }
              }
              // Use the first page as reference for propagation
              this.updateSectionChangeToEveryPage(firstPage.id, sectionType)
            }
          }

          if (
            (sectionType === PAGE_SECTIONS.HEADERS || sectionType === PAGE_SECTIONS.FOOTERS) &&
            shouldScheduleFloatingLayoutAfterOption(key)
          ) {
            scheduleAllPagesFloatingLayoutSync(this)
          }

          this.createRedoStack()
          return
        }
      }
    },
    updateGroupStyle(params: IUpdateElementOption) {
      if (!this.isDocumentEditable) return
      const { id, value, key, type } = params
      if (type === GROUP_LOOKUP.GROUP_FIELDS) {
        const groupIndex = this.groups.findIndex(g => g.id === id)
        if (groupIndex > -1) this.groups[groupIndex].responsiveStyles.large[key as keyof IResponsiveStylesOptions] = value
        this.createRedoStack()
      }
      return
    },
    assignRecipientToEverySignatureElement(recipient: IRecipient) {
      if (!this.isDocumentEditable) return
      if (!recipient.id) return

      const assignRecipient = (elements: IElement[]) => {
        return elements.map(child => {
          if (child.type === ELEMENTS_LOOKUP.SIGNATURE) {
            return {
              ...child,
              component: {
                ...child.component,
                options: {
                  ...child.component.options,
                  [ELEMENTS_META.RECIPIENT]: recipient.id,
                },
              },
            }
          }
          return child
        })
      }

      this.pages = this.pages.map(page => {
        const updatedPage = {
          ...page,
          children: assignRecipient(page.children ?? []),
        }

        if (page.component.options.headers && updatedPage?.component?.options?.headers?.children) {
          updatedPage.component.options.headers.children = assignRecipient(page.component.options.headers.children ?? [])
        }

        if (page.component.options?.footers && updatedPage?.component?.options?.footers?.children) {
          updatedPage.component.options.footers.children = assignRecipient(page.component.options.footers.children ?? [])
        }

        return updatedPage
      })
      this.createRedoStack()
    },
    autoAssignIfRecipientIdIsNotPresentInRecipientList() {
      if (!this.isDocumentEditable) return

      const appStore = useAppStore()
      const primaryRecipient = appStore.document?.recipients.find(r => r.isPrimary)
      if (!primaryRecipient) return

      const { RECIPIENT, ENTITY_NAME } = ELEMENTS_META

      const assignRecipient = (options: any) => {
        const recipientExist = appStore.document?.recipients.some(r => r.id === options[RECIPIENT] && r.entityName === options[ENTITY_NAME])

        if (!recipientExist || !options[RECIPIENT] || !options[ENTITY_NAME]) {
          return {
            ...options,
            [RECIPIENT]: primaryRecipient.id,
            [ENTITY_NAME]: primaryRecipient.entityName,
          }
        }
        return options
      }

      const autoAssign = (children: IElement[]): IElement[] => {
        return children.map(child => {
          if (assignableFields.has(child?.type)) {
            child.component.options = assignRecipient(child.component.options)
          }
          if (child?.children && child?.children?.length) {
            child.children = autoAssign(child.children)
          }
          return child
        })
      }

      this.pages.forEach(page => {
        page.children = autoAssign(page.children ?? [])
        const headerChildren = page.component?.options?.[PAGE_SECTIONS.HEADERS]?.children
        if (headerChildren) {
          page.component.options[PAGE_SECTIONS.HEADERS].children = autoAssign(headerChildren)
        }
        const footerChildren = page.component?.options?.[PAGE_SECTIONS.FOOTERS]?.children
        if (footerChildren) {
          page.component.options[PAGE_SECTIONS.FOOTERS].children = autoAssign(footerChildren)
        }
      })
      this.groups.forEach(group => {
        group.component.options = assignRecipient(group.component.options)
        group.children = group.children.map(child => {
          if (assignableFields.has(child?.type)) {
            child.component.options = assignRecipient(child.component.options)
          }
          return child
        })
      })

      this.createRedoStack()
    },
    autoAssignSenderIfNotPresentInRecipientList() {
      if (!this.isDocumentEditable) return

      const appStore = useAppStore()
      const currentUser = getRecipientParamsFromContact(appStore.userData, RecipientEntityTypeEnum.USERS, false)

      const { RECIPIENT, ENTITY_NAME } = ELEMENTS_META

      const assignRecipient = (options: any) => {
        if (options.recipient === DOCUMENT_SENDER_ID) {
          return {
            ...options,
            [RECIPIENT]: currentUser.id,
            [ENTITY_NAME]: currentUser.entityName,
          }
        }

        return options
      }

      const autoAssign = (children: IElement[]): IElement[] => {
        return children.map(child => {
          if (assignableFields.has(child?.type)) {
            child.component.options = assignRecipient(child.component.options)
            if (child?.children && child?.children?.length) {
              child.children = autoAssign(child.children)
            }
          } else if (child?.children && child?.children?.length) {
            child.children = autoAssign(child.children)
          }
          return child
        })
      }

      this.pages.forEach(page => {
        page.children = autoAssign(page.children ?? [])
        const headerChildren = page.component?.options?.[PAGE_SECTIONS.HEADERS]?.children
        if (headerChildren) {
          page.component.options[PAGE_SECTIONS.HEADERS].children = autoAssign(headerChildren)
        }
        const footerChildren = page.component?.options?.[PAGE_SECTIONS.FOOTERS]?.children
        if (footerChildren) {
          page.component.options[PAGE_SECTIONS.FOOTERS].children = autoAssign(footerChildren)
        }
      })
      this.groups.forEach(group => {
        group.component.options = assignRecipient(group.component.options)
        group.children = group.children.map(child => {
          if (assignableFields.has(child?.type)) {
            child.component.options = assignRecipient(child.component.options)
            if (child && child?.component) {
              child.component.options = assignRecipient(child.component.options)
            }
          } else if (child?.children && child?.children?.length) {
            child.children = autoAssign(child.children)
          }
          return child
        })
      })
      this.createRedoStack()
    },
    getAllFillableElements() {
      return flatChildrens(this.pages).filter((element): element is IElement => element != null && assignableFields.has(element.type))
    },
    syncContactIdInLinkedCustomValues() {
      const customValueLinkage = this.customValueLinkage
      const fillableElementMap = new Map<string, any>()
      flatChildrens(this.pages).forEach(element => {
        if (element && fillableFields.has(element.type)) {
          fillableElementMap.set(element.id, element)
        }
      })
      Object.keys(customValueLinkage).forEach(elementId => {
        const element = fillableElementMap.get(elementId)
        if (element && element?.component?.options?.recipient) {
          customValueLinkage[elementId].contactId = element.component.options.recipient
        }
      })
    },
    getContactCustomValueLinkMapping() {
      const customValueLinkage = this.customValueLinkage
      const data = Object.keys(customValueLinkage).reduce(
        (acc, elementId) => {
          const customFields = customValueLinkage[elementId].customFields
          const contactId = customValueLinkage[elementId].contactId
          if (contactId) {
            acc[contactId] = acc[contactId] ? [...acc[contactId], ...customFields] : [...customFields]
          }
          return acc
        },
        {} as Record<string, any>
      )
      return data
    },
    setActiveElementId(id: string) {
      if (!this.isDocumentEditable) return
      this.activeElementId = id
      // Update section activation state
      this.updateSectionActivationState()
    },
    syncRecipient() {
      const appStore = useAppStore()
      const { updateDocumentOption } = useElementOption()
      const recipients = appStore.document?.recipients
      const fillableElements = this.getAllFillableElements()
      const fillableElementsNotPartOfAnyGroup = removeFillableElementsWhichIsPartOfGroup(fillableElements, this.groups)

      const allAssignableFieldsWithGroups = [...this.groups, ...fillableElementsNotPartOfAnyGroup]

      const allRecipient = removeUserFromRecipientIfElementIsNotAssigned(allAssignableFieldsWithGroups, recipients)

      const userData = appStore.userData
      const otherRecipients = userData ? getAdditionalRecipientsWithRoles(allRecipient, fillableElements, userData) : []

      let mergedRecipients = [...allRecipient]
      if (otherRecipients.length > 0) {
        mergedRecipients = [...allRecipient, ...otherRecipients]
      }

      const allRecipientsWithRoles = updateRecipientRolesIfElementIsAssigned(allAssignableFieldsWithGroups, mergedRecipients)

      updateDocumentOption(APP_STORE_META.RECIPIENTS, allRecipientsWithRoles)
    },
    syncGroupRecipient() {
      const updateNestedChild = (element: IElement, ids: string[], groupOptions: any) => {
        if (!element.children) return
        element.children.forEach(child => {
          if (ids.includes(child.id)) {
            // For card save element business user is not allowed by payment widget
            // Skip assignment if element is SaveCardDetails and recipient is a user
            if (child.type === ELEMENTS_LOOKUP.SAVE_CARD_DETAILS && groupOptions.entityName === RecipientEntityTypeEnum.USERS) {
              return
            }
            child.component.options.recipient = groupOptions.recipient
            child.component.options.entityName = groupOptions.entityName
          }
          if (child.children) {
            updateNestedChild(child, ids, groupOptions)
          }
        })
      }
      const applyGroupToSectionChildren = (
        children: IElement[] | undefined,
        g: { component: { options: { recipient: string; entityName?: string } }; children: { elementId: string }[] }
      ) => {
        if (!children) return
        const elementIds = g.children.map(({ elementId }) => elementId)
        children.forEach(element => {
          if (elementIds.includes(element.id)) {
            element.component.options.recipient = g.component.options.recipient
            element.component.options.entityName = g.component.options.entityName
          } else {
            updateNestedChild(element, elementIds, g.component.options)
          }
        })
      }
      this.groups.forEach(g => {
        const elementIds = g.children.map(({ elementId }) => elementId)
        if (g.component.options.recipient && g.component.options.entityName) {
          this.pages.forEach(page => {
            page.children?.forEach(element => {
              if (elementIds.includes(element.id)) {
                // For card save element business user is not allowed by payment widget
                // Skip assignment if element is SaveCardDetails and recipient is a user
                if (
                  element.type === ELEMENTS_LOOKUP.SAVE_CARD_DETAILS &&
                  g.component.options.entityName === RecipientEntityTypeEnum.USERS
                ) {
                  return
                }
                element.component.options.recipient = g.component.options.recipient
                element.component.options.entityName = g.component.options.entityName
              } else {
                updateNestedChild(element, elementIds, g.component.options)
              }
            })
            applyGroupToSectionChildren(page.component?.options?.[PAGE_SECTIONS.HEADERS]?.children, g)
            applyGroupToSectionChildren(page.component?.options?.[PAGE_SECTIONS.FOOTERS]?.children, g)
          })
        }
      })
    },
    syncCurrency(currencyCode: string) {
      if (!this.isDocumentEditable) return
      this.pages = this.pages.map(page => {
        return Object.assign(page, {
          children: page.children?.map(child => {
            if (child.type === ELEMENTS_LOOKUP.PRODUCT_LIST) {
              return {
                ...child,
                component: {
                  ...child.component,
                  options: {
                    ...child.component.options,
                    [ELEMENTS_META.CURRENCY_CODE]: currencyCode,
                    [ELEMENTS_META.LINE_ITEMS]: child.component.options.lineItems.map((lineItem: any) => ({
                      ...lineItem,
                      currency: currencyCode,
                    })),
                  },
                },
              }
            }
            if (child.type === ELEMENTS_LOOKUP.ROW) {
              return {
                ...child,
                children: child.children?.map(column => {
                  return {
                    ...column,
                    children: column.children?.map(el => {
                      if (el.type === ELEMENTS_LOOKUP.PRODUCT_LIST) {
                        return {
                          ...el,
                          component: {
                            ...el.component,
                            options: {
                              ...el.component.options,
                              [ELEMENTS_META.CURRENCY_CODE]: currencyCode,
                              [ELEMENTS_META.LINE_ITEMS]: el.component.options.lineItems.map((lineItem: any) => ({
                                ...lineItem,
                                currency: currencyCode,
                              })),
                            },
                          },
                        }
                      } else {
                        return el
                      }
                    }),
                  }
                }),
              }
            }
            return child
          }),
        })
      })
    },
    syncGroup() {
      const elementIds = flatChildrens(this.pages).map(({ id }) => id)

      // filtering & updating the group elements, if the element is available in the pages.
      this.groups.forEach(g => {
        g.children = g.children.filter(({ elementId }) => elementIds.includes(elementId))
      })

      // deleting the group if its having child length of 1 or less.
      this.groups = this.groups.filter(g => g.children?.length > 1)
    },
    getFillableFields() {
      const fillableFields = new Set([
        ELEMENTS_LOOKUP.TEXT_FIELD,
        ELEMENTS_LOOKUP.DATE_FIELD,
        ELEMENTS_LOOKUP.CHECKBOX,
        ELEMENTS_LOOKUP.SAVE_CARD_DETAILS,
      ])
      return flatChildrens(this.pages)
        .filter(child => fillableFields.has(child.type))
        .map(child => {
          const {
            component: { options: option },
          } = child
          return {
            value: option.text || ('' as string),
            fieldId: option.fieldId as string,
            isRequired: option.required as boolean,
            recipient: option.recipient,
            hasCompleted: option.text ? true : false,
            entityType: option.entityName as RecipientEntityTypeEnum,
            id: child.id,
            type: child.type,
          }
        }) as UpdateSignatureParams['fillableFields']
    },
    getAllProductList() {
      return flatChildrens(this.pages).filter(element => element.type === ELEMENTS_LOOKUP.PRODUCT_LIST)
    },
    setFlatCustomValues(values: any) {
      this.flatCustomValues = values
    },
    isRecurringProduct() {
      return flatChildrens(this.pages)
        .filter(child => child.type === ELEMENTS_LOOKUP.PRODUCT_LIST) // Filter for relevant children
        .map(child =>
          (child.component?.options as ProductListElementOptions)?.lineItems?.find(
            (item: LineItem) => item.priceType === PriceTypeEnum.RECURRING && !item?.isSetupFeeItem
          )
        )
        .find((lineItem): lineItem is LineItem => !!lineItem)
    },
    hasNoLineItems() {
      return flatChildrens(this.pages)
        .filter(child => child.type === ELEMENTS_LOOKUP.PRODUCT_LIST) // Filter for relevant children
        .map(child => (child.component?.options as ProductListElementOptions)?.lineItems?.length)
        .every(length => length === 0)
    },
    syncCustomFieldsForLinkage() {
      const keys = Object.keys(this.customValueLinkage)
      // if the key is not exist in the page fillable fields then remove the key from the custom value linkage
      const fillableElements = flatChildrens(this.pages).filter(element => fillableFields.has(element.type))

      // iterate over the custom value linkage and check
      // if the key is not exist in the fillable elements then remove the key from the custom value linkage
      keys.forEach(key => {
        const isKeyExist = fillableElements.some(element => element.id === key)
        if (!isKeyExist) {
          delete this.customValueLinkage[key]
        }
      })

      // if the primary recipient is there then only do the below operation.
      // otherwise, cloneFromTheTemplate will have an issue.
      const appStore = useAppStore()
      const primaryContact = appStore?.primaryContact
      if (primaryContact && primaryContact.id) {
        fillableElements.forEach(element => {
          if (this.customValueLinkage[element.id]) {
            const contactId = this.customValueLinkage[element.id].contactId
            if (!contactId) {
              delete this.customValueLinkage[element.id]
            }
            if (contactId && element.component?.options?.recipient) {
              if (element.component?.options?.entityName !== RecipientEntityTypeEnum.CONTACTS) {
                delete this.customValueLinkage[element.id]
              }

              if (element.component?.options?.recipient && contactId && element.component?.options?.recipient !== contactId) {
                this.customValueLinkage[element.id].contactId = element.component?.options?.recipient
              }
            }
          }
        })
      }
    },
    deleteElementFromNestedChildren(id: string) {
      if (!this.isDocumentEditable) return
      const findAndDelete = children => {
        for (let i = 0; i < children.length; i++) {
          if (children[i]?.id === id) {
            children?.splice(i, 1)
            return true
          }
          if (children[i]?.children && children[i]?.children?.length) {
            const found = findAndDelete(children[i].children)
            if (found) return true
          }
        }
        return false
      }

      this.pages.forEach(page => {
        findAndDelete(page.children)
      })
      this.createRedoStack()
    },
    addElementToElementChildren(parentId: string, newElement: IElement) {
      if (!this.isDocumentEditable) return

      const findAndAdd = children => {
        for (let i = 0; i < children.length; i++) {
          if (children[i].id === parentId) {
            children[i].children = children[i].children || []
            children[i].children.push(newElement)
            return true
          }
          if (children[i].children && children[i].children.length) {
            const found = findAndAdd(children[i].children)
            if (found) return true
          }
        }
        return false
      }

      // Check if parentId belongs to a Page object
      for (const page of this.pages) {
        if (page.id === parentId) {
          page.children = page.children || []
          page.children.push(newElement)
          return
        }

        // Otherwise, search within the children of the Page
        if (findAndAdd(page.children)) {
          return
        }
      }
      this.createRedoStack()
    },
    moveElementById(elementId: string, newParentId: string, sectionType?: string | null) {
      // Normalise the section marker. `getElementSection` returns `PAGE_SECTIONS.CONTENT` ('content')
      // for elements living in `page.children`, but this function — and all the downstream section
      // branches — only understand `HEADERS` / `FOOTERS` / null. Treating `'content'` as a "real"
      // section caused `findAndAdd` to fail and the fallback branch to push the element into the
      // header (when the caller only wanted to move the fillable to page content level).
      if (sectionType === PAGE_SECTIONS.CONTENT) sectionType = null
      let elementToMove: IElement | null = null

      // Build the set of element IDs that represent the SAME logical fillable across all pages.
      // The caller passes a single `elementId` (typically the canonical page-0 id), but the same
      // fillable lives on every page with a per-page clone id registered in `headerFooterCloneIdMap`.
      // Without this set, `findAndRemove` only removes the element from the page that actually
      // owns `elementId`; the clones on other pages stay behind as orphans at top level. When
      // `updateSectionChangeToEveryPage` then propagates from a non-canonical host (because the
      // user dropped on a non-canonical page), those orphans get cloned back onto every page —
      // including the next "+ Add page" — and you end up with a duplicated fillable that visually
      // covers the legitimate Text block and breaks header rendering.
      const cloneMap = this.headerFooterCloneIdMap ?? {}
      const idsToRemove = new Set<string>([elementId])
      let canonicalSourceId: string | null = null
      if (cloneMap[elementId]) {
        canonicalSourceId = elementId
      } else {
        for (const [srcId, perPage] of Object.entries(cloneMap)) {
          if (perPage && Object.values(perPage).includes(elementId)) {
            canonicalSourceId = srcId
            break
          }
        }
      }
      if (canonicalSourceId && cloneMap[canonicalSourceId]) {
        idsToRemove.add(canonicalSourceId)
        for (const cloneId of Object.values(cloneMap[canonicalSourceId])) {
          if (cloneId) idsToRemove.add(cloneId)
        }
      }

      const findAndRemove = (children: IElement[]): IElement[] => {
        return children
          .map((child: IElement) => {
            if (child.id === elementId) {
              // Capture only the canonical-id match for re-insertion; replicas are dropped silently.
              elementToMove = child
              return null
            }
            if (idsToRemove.has(child.id)) {
              // Replica of the same logical fillable on a non-canonical page — strip it but don't
              // overwrite `elementToMove`; the canonical-id match is what we want to re-parent.
              return null
            }

            if (child.children && child.children.length > 0) {
              child.children = findAndRemove(child.children).filter(Boolean)
            }
            return child
          })
          .filter(Boolean) as IElement[]
      }

      this.pages.forEach(page => {
        // Check in content section
        page.children = findAndRemove(page.children)

        // Check in header section
        if (page.component?.options[PAGE_SECTIONS.HEADERS]?.children) {
          page.component.options[PAGE_SECTIONS.HEADERS].children = findAndRemove(page.component.options[PAGE_SECTIONS.HEADERS].children)
        }

        // Check in footer section
        if (page.component?.options[PAGE_SECTIONS.FOOTERS]?.children) {
          page.component.options[PAGE_SECTIONS.FOOTERS].children = findAndRemove(page.component.options[PAGE_SECTIONS.FOOTERS].children)
        }
      })

      if (elementToMove) {
        const findAndAdd = (children: IElement[]): boolean => {
          for (let i = 0; i < children.length; i++) {
            if (children[i]?.id === newParentId) {
              children[i].children = children[i].children || []
              children[i].children.push(elementToMove as IElement)
              return true
            }

            if (children[i]?.children && children[i].children.length > 0) {
              const found = findAndAdd(children[i].children)
              if (found) return true
            }
          }
          return false
        }

        let elementAdded = false
        let addedToSectionPageId: string | null = null
        let addedToSectionType: string | null = null

        for (const page of this.pages) {
          // If sectionType is provided and newParentId matches page ID, add directly to that section
          if (sectionType && page?.id === newParentId) {
            if (sectionType === PAGE_SECTIONS.HEADERS) {
              if (!page.component.options[PAGE_SECTIONS.HEADERS]) {
                page.component.options[PAGE_SECTIONS.HEADERS] = createElement({
                  type: ELEMENTS_LOOKUP.PAGE_SECTION,
                })
              }
              if (!page.component.options[PAGE_SECTIONS.HEADERS].children) {
                page.component.options[PAGE_SECTIONS.HEADERS].children = []
              }
              page.component.options[PAGE_SECTIONS.HEADERS].children.push(elementToMove)
              elementAdded = true
              addedToSectionPageId = page.id
              addedToSectionType = sectionType
              break
            } else if (sectionType === PAGE_SECTIONS.FOOTERS) {
              if (!page.component.options[PAGE_SECTIONS.FOOTERS]) {
                page.component.options[PAGE_SECTIONS.FOOTERS] = createElement({
                  type: ELEMENTS_LOOKUP.PAGE_SECTION,
                })
              }
              if (!page.component.options[PAGE_SECTIONS.FOOTERS].children) {
                page.component.options[PAGE_SECTIONS.FOOTERS].children = []
              }
              page.component.options[PAGE_SECTIONS.FOOTERS].children.push(elementToMove)
              elementAdded = true
              addedToSectionPageId = page.id
              addedToSectionType = sectionType
              break
            }
          }

          // Try to add to content section
          if (page?.id === newParentId && !sectionType) {
            page.children = page.children || []
            page.children.push(elementToMove)
            elementAdded = true
            break
          }

          if (sectionType === PAGE_SECTIONS.FOOTERS) {
            if (page.component?.options[PAGE_SECTIONS.FOOTERS]?.children) {
              if (findAndAdd(page.component.options[PAGE_SECTIONS.FOOTERS].children)) {
                elementAdded = true
                addedToSectionPageId = page.id
                addedToSectionType = PAGE_SECTIONS.FOOTERS
                break
              }
            }
          } else if (sectionType === PAGE_SECTIONS.HEADERS) {
            if (page.component?.options[PAGE_SECTIONS.HEADERS]?.children) {
              if (findAndAdd(page.component.options[PAGE_SECTIONS.HEADERS].children)) {
                elementAdded = true
                addedToSectionPageId = page.id
                addedToSectionType = PAGE_SECTIONS.HEADERS
                break
              }
            }
          } else {
            if (findAndAdd(page.children || [])) {
              elementAdded = true
              break
            }

            if (page.component?.options[PAGE_SECTIONS.HEADERS]?.children) {
              if (findAndAdd(page.component.options[PAGE_SECTIONS.HEADERS].children)) {
                elementAdded = true
                addedToSectionPageId = page.id
                addedToSectionType = PAGE_SECTIONS.HEADERS
                break
              }
            }

            if (page.component?.options[PAGE_SECTIONS.FOOTERS]?.children) {
              if (findAndAdd(page.component.options[PAGE_SECTIONS.FOOTERS].children)) {
                elementAdded = true
                addedToSectionPageId = page.id
                addedToSectionType = PAGE_SECTIONS.FOOTERS
                break
              }
            }
          }
        }

        if (addedToSectionPageId && addedToSectionType) {
          this.updateSectionChangeToEveryPage(addedToSectionPageId, addedToSectionType)
          // Refresh canonical section reference. `updateSectionChangeToEveryPage(addedToSectionPageId, …)`
          // uses `addedToSectionPageId` as the propagation host, which means EVERY OTHER page —
          // including page 0 (the canonical source) when the drop happened on a non-canonical page —
          // gets `page.component.options[sectionType]` REPLACED with a fresh clone object. After that
          // replacement, `this.headerSection` / `this.footerSection` (set earlier from page 0's section)
          // is an orphaned reference whose `.children` no longer reflect the nested re-parent we just
          // did. The next `syncHeaderFooterToAllPages` (e.g. user clicks "+ Add page" right after
          // nesting a fillable inside a Text block in the header) would then clone the STALE reference
          // to the new page and the fillable would be missing. Re-pointing here fixes that.
          if (addedToSectionType === PAGE_SECTIONS.HEADERS) {
            this.headerSection = this.pages[0]?.component?.options?.[PAGE_SECTIONS.HEADERS]
          } else if (addedToSectionType === PAGE_SECTIONS.FOOTERS) {
            this.footerSection = this.pages[0]?.component?.options?.[PAGE_SECTIONS.FOOTERS]
          }
        }

        // If element wasn't added anywhere, add it back to its original location as fallback.
        // Respect the caller's intent: if `sectionType` is HEADERS/FOOTERS, prefer that section.
        // Otherwise prefer `page.children` (content area). Preferring header first — the previous
        // behaviour — meant a fillable dropped on the content area of a page that happened to have
        // a header got snapped into the header on the drop, which is the exact bug a user reported
        // for fillables nested inside a TEXT block in `page.children`.
        if (!elementAdded && elementToMove) {
          for (const page of this.pages) {
            if (sectionType === PAGE_SECTIONS.HEADERS) {
              if (page.component?.options[PAGE_SECTIONS.HEADERS]?.children) {
                page.component.options[PAGE_SECTIONS.HEADERS].children.push(elementToMove)
                elementAdded = true
                break
              }
              continue
            }
            if (sectionType === PAGE_SECTIONS.FOOTERS) {
              if (page.component?.options[PAGE_SECTIONS.FOOTERS]?.children) {
                page.component.options[PAGE_SECTIONS.FOOTERS].children.push(elementToMove)
                elementAdded = true
                break
              }
              continue
            }
            // No explicit section requested → content area wins. Only fall back to a header/footer
            // if the page has no content area (which should never happen, but keeps the safety net).
            page.children = page.children || []
            page.children.push(elementToMove)
            elementAdded = true
            break
          }
          if (!elementAdded && elementToMove) {
            for (const page of this.pages) {
              if (page.component?.options[PAGE_SECTIONS.FOOTERS]?.children) {
                page.component.options[PAGE_SECTIONS.FOOTERS].children.push(elementToMove)
                elementAdded = true
                break
              }
              if (page.component?.options[PAGE_SECTIONS.HEADERS]?.children) {
                page.component.options[PAGE_SECTIONS.HEADERS].children.push(elementToMove)
                elementAdded = true
                break
              }
            }
          }
        }

        this.createRedoStack()
      } else {
        this.createRedoStack()
      }
    },
    moveFloatingElement(
      elementId: string,
      options: {
        elementId: string
        newParentId: string
        sectionType?: string | null
        styles: {
          top: number
          left: number
          bottom: number
          right: number
          height: number
          width: number
          preferBottom: boolean
          preferRight: boolean
        }
      }
    ) {
      // First, check if the element is in a section
      const currentSectionType = this.getElementSection(elementId)
      let currentPageId = currentSectionType
        ? this.getPageIdBySectionElementId(elementId, currentSectionType)
        : this.getPageIdByElementId(elementId)
      if (currentSectionType && !currentPageId) {
        currentPageId = this.getPageIdByElementId(elementId)
      }

      // Header/footer `.page-section` roots both use the host page id as `data-id`. Moveable can
      // attribute a drag to the wrong section; do not reparent on that false positive.
      const pageHostId = this.getPageIdByElementId(elementId)
      const movableWrongHeaderFooterRoot = Boolean(
        pageHostId &&
        options.newParentId === pageHostId &&
        currentSectionType &&
        options.sectionType &&
        (currentSectionType === PAGE_SECTIONS.HEADERS || currentSectionType === PAGE_SECTIONS.FOOTERS) &&
        (options.sectionType === PAGE_SECTIONS.HEADERS || options.sectionType === PAGE_SECTIONS.FOOTERS) &&
        options.sectionType !== currentSectionType
      )

      // If element is being moved to a different parent or section, use moveElementById
      // Otherwise, just update the position styles (element stays in same location)
      let isMovingToDifferentParent = options.newParentId !== currentPageId || options.sectionType !== currentSectionType
      if (movableWrongHeaderFooterRoot) {
        isMovingToDifferentParent = false
      }

      // Split-column-specific lift in H/F sections.
      //
      // Scenario: a fillable lives nested inside a Text/Image/etc. block that is itself
      // inside one of the columns of a split row (section > Row > Column > Block > Float).
      // The user drags the fillable so it spans BOTH columns. `getNearestSnap`'s `isInside`
      // check fails on every nested boundBox (none of the columns' inner blocks fully
      // contain the float), so the snap falls back to the PageSection root. The emitted
      // payload becomes `newParentId = pageId, sectionType = 'headers' | 'footers'` —
      // intent: "place at top of section.children".
      //
      // The default `isMovingToDifferentParent` check compares `newParentId` to
      // `currentPageId` (the page that *owns* the float, not its immediate parent). Both
      // are the same pageId here, so the check returns false and `moveElementById` never
      // fires. The float stays structurally inside the column's Text block, but the
      // position written is PageSection-relative. After re-render the float's CSS
      // containing block is the Text block's `<section class="boundBox">` (it carries
      // `transform: translateZ(0)`, which creates a containing block for absolute
      // descendants) — so PageSection-relative `top/left` get rendered Text-relative
      // and the float visibly jumps to the extreme right/bottom by the column's
      // horizontal offset.
      //
      // The fix is intentionally narrow: only fire when the element is currently nested
      // inside a Row > Column subtree of the H/F section. Non-split nesting (a Text block
      // at the top of section.children with a float inside) does NOT take this path —
      // those drags resolve through the regular block-snap and `isMovingToDifferentParent`
      // already returns true via the normal `newParentId !== currentPageId` comparison.
      if (
        !isMovingToDifferentParent &&
        (options.sectionType === PAGE_SECTIONS.HEADERS || options.sectionType === PAGE_SECTIONS.FOOTERS) &&
        options.newParentId === currentPageId
      ) {
        const hostPage = this.pages.find(p => p.id === currentPageId)
        const sectionChildren = (hostPage?.component?.options?.[options.sectionType] as IElement | undefined)?.children ?? []

        // Is the element currently inside any Row > Column subtree in this section?
        // `elementIdInSubtree` is a deep-walk helper — it finds the id at any nesting
        // depth, so a float buried inside Column > Text > Float is still detected.
        let isInsideSplitColumn = false
        for (const sectionChild of sectionChildren) {
          if (sectionChild?.type !== ELEMENTS_LOOKUP.ROW || !Array.isArray(sectionChild.children)) continue
          for (const col of sectionChild.children) {
            if (col?.type !== ELEMENTS_LOOKUP.COLUMN || !Array.isArray(col.children)) continue
            if (elementIdInSubtree(col.children, elementId)) {
              isInsideSplitColumn = true
              break
            }
          }
          if (isInsideSplitColumn) break
        }

        if (isInsideSplitColumn) {
          // Force the reparent so `moveElementById` runs and hits its
          // `sectionType && page.id === newParentId` branch (line 2924) — that branch
          // calls `findAndRemove` (which strips the float from its Column subtree) and
          // then pushes it to `section.children[]` top level. After re-render the float
          // sits at the PageSection level, its CSS containing block is the PageSection
          // root (which has `position: relative` for H/F sections), and the stored
          // PageSection-relative coords finally agree with the render space.
          isMovingToDifferentParent = true
        }
      }

      // Only HEADERS / FOOTERS are valid section markers for reparenting. `PAGE_SECTIONS.CONTENT`
      // is a sentinel returned by `getElementSection` for page.children elements and must NOT leak
      // into `moveElementById` — it confuses the section-scoped search and routes the element into
      // the header via the fallback. Normalise here.
      const normalizeSectionMarker = (st: string | null | undefined) =>
        st === PAGE_SECTIONS.HEADERS || st === PAGE_SECTIONS.FOOTERS ? st : null
      const sectionForReparent = normalizeSectionMarker(options.sectionType) ?? normalizeSectionMarker(currentSectionType)

      if (isMovingToDifferentParent) {
        // Bound blocks rarely carry data-section-type; fall back to the element's current section
        // so reparenting searches the correct header/footer tree (avoids header-first duplicate ids).
        this.moveElementById(elementId, options.newParentId, sectionForReparent ?? undefined)
      }

      // Always update position styles (even if not moving parent)
      const element = flatChildrens(this.pages).find(el => el.id === elementId)
      if (!element) {
        // Element not found - this shouldn't happen, but if it does, return early
        return
      }

      // Same normalisation as above — only HEADERS/FOOTERS are real sections for style routing.
      // `PAGE_SECTIONS.CONTENT` leaking in would route to `updateSectionElementStyle('content')`,
      // which silently fails because `page.component.options.content` doesn't exist, leaving the
      // just-moved element at its old coordinates.
      const sectionForStyles = normalizeSectionMarker(this.getElementSection(elementId)) ?? normalizeSectionMarker(currentSectionType)

      // Use updateSectionElementStyle if element is in a real header/footer section, otherwise use updateElementStyle
      if (sectionForStyles) {
        this.updateSectionElementStyle({
          id: elementId,
          key: 'position',
          value: {
            top: options.styles.top,
            left: options.styles.left,
            bottom: options.styles.bottom,
            right: options.styles.right,
            preferBottom: options.styles.preferBottom,
            preferRight: options.styles.preferRight,
          },
          section: sectionForStyles,
        })

        // Update dimensions if provided
        if (options.styles.height && options.styles.width) {
          this.updateSectionElementStyle({
            id: elementId,
            key: 'dimensions',
            value: {
              height: options.styles.height,
              width: options.styles.width,
            },
            section: sectionForStyles,
          })
        }
      } else {
        this.updateElementStyle({
          id: elementId,
          key: 'position',
          value: {
            top: options.styles.top,
            left: options.styles.left,
            bottom: options.styles.bottom,
            right: options.styles.right,
            preferBottom: options.styles.preferBottom,
            preferRight: options.styles.preferRight,
          },
          type: element.type,
        })

        // Update dimensions if provided
        if (options.styles.height && options.styles.width) {
          this.updateElementStyle({
            id: elementId,
            key: 'dimensions',
            value: {
              height: options.styles.height,
              width: options.styles.width,
            },
            type: element.type,
          })
        }
      }

      // Clear isGhost flag after drag ends
      this.updateElementOption({
        id: elementId,
        key: 'isGhost',
        value: false,
      })
    },
    setAllLineItemsAndQtyOptional(value: boolean) {
      if (!this.isDocumentEditable) return
      this.pages = this.pages.map(page => {
        return Object.assign(page, {
          children: page.children?.map(child => {
            if (child.type === ELEMENTS_LOOKUP.PRODUCT_LIST) {
              return {
                ...child,
                component: {
                  ...child.component,
                  options: {
                    ...child.component.options,
                    [ELEMENTS_META.LINE_ITEMS]: child.component.options?.lineItems.map((lineItem: LineItem) => ({
                      ...lineItem,
                      optional: value,
                      qtyEditable: value,
                      // if optional value is false, then we need  disabled: true, selected: true
                      ...(value
                        ? { disabled: false }
                        : {
                            disabled: true,
                            selected: true,
                          }),
                    })),
                  },
                },
              }
            } else if (child.type === ELEMENTS_LOOKUP.ROW) {
              return {
                ...child,
                children: child.children?.map(column => {
                  return {
                    ...column,
                    children: column.children?.map(el => {
                      if (el.type === ELEMENTS_LOOKUP.PRODUCT_LIST) {
                        return {
                          ...el,
                          component: {
                            ...el.component,
                            options: {
                              ...el.component.options,
                              [ELEMENTS_META.LINE_ITEMS]: el.component.options?.lineItems.map((lineItem: LineItem) => ({
                                ...lineItem,
                                optional: value,
                                qtyEditable: value,
                                ...(value
                                  ? { disabled: false }
                                  : {
                                      disabled: true,
                                      selected: true,
                                    }),
                              })),
                            },
                          },
                        }
                      } else {
                        return el
                      }
                    }),
                  }
                }),
              }
            }
            return child
          }),
        })
      })
    },
    setLoadedFonts(fonts: string[]) {
      this.loadedFonts = fonts
    },
    setLoadedFontsFromEditor(fonts: string[]) {
      const uniqueFonts = [...new Set([...fonts, ...(this.fontsToLoad ? this.fontsToLoad : [])])]
      this.fontsToLoad = uniqueFonts
    },
    getCustomValueLinkageErrors() {
      this.syncCustomFieldsForLinkage()
      const appStore = useAppStore()
      const contactCustomValueMapping = this.getContactCustomValueLinkMapping()
      const recipients = appStore?.document?.recipients || []
      const customValueLinkageErrors = checkForDuplicatesFieldKeyInCustomValueLinkage(contactCustomValueMapping, recipients)
      return customValueLinkageErrors || []
    },
    getImageSizeErrors(): string[] {
      const errors: string[] = []
      const elements = flatChildrens(this.pages)
      elements.forEach((child: IElement) => {
        if (!child || child.type !== ELEMENTS_LOOKUP.IMAGE) return

        const src = (child as any)?.component?.options?.src
        if (!src) return

        const height = child?.responsiveStyles?.large?.height
        const width = child?.responsiveStyles?.large?.width

        const heightNum = height !== '' && height !== undefined && height !== null ? Number(height) : NaN
        const widthNum = width !== '' && width !== undefined && width !== null ? Number(width) : NaN

        const invalid = !height || !width || Number.isNaN(heightNum) || Number.isNaN(widthNum) || heightNum <= 0 || widthNum <= 0

        if (!invalid) return

        errors.push((child as any)?.id)
      })
      return errors
    },
    recomputeFloatingLayoutPositionsForPage(pageId: string) {
      if (typeof document === 'undefined') return
      const pageIndex = this.pages.findIndex(p => p.id === pageId)
      if (pageIndex < 0) return
      const page = this.pages[pageIndex]
      const ids = collectBottomPreferDraggableIdsFromPage(page)
      if (!ids.length) return
      nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            for (const fid of ids) {
              const el = findElementOnSinglePage(page, fid)
              if (!el?.component?.isDraggable) continue
              const pos = el.responsiveStyles?.large?.position
              if (!pos?.preferBottom) continue
              const sectionForMeasure = getFloatingBoundSectionForElementOnPage(page, fid)
              const geometry = computeBottomPreferGeometryFromStoredLayout(el, pageIndex, sectionForMeasure)
              if (!geometry) continue
              if (!bottomPreferLayoutNeedsWrite(pos, geometry)) continue
              applyBottomPreferPositionSync(el, geometry, !!pos.preferRight)
            }
          })
        })
      })
    },
    recomputeFloatingLayoutPositionsForAllPages() {
      if (typeof document === 'undefined') return
      nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            for (let pageIndex = 0; pageIndex < this.pages.length; pageIndex++) {
              const page = this.pages[pageIndex]
              const ids = collectBottomPreferDraggableIdsFromPage(page)
              for (const fid of ids) {
                const el = findElementOnSinglePage(page, fid)
                if (!el?.component?.isDraggable) continue
                const pos = el.responsiveStyles?.large?.position
                if (!pos?.preferBottom) continue
                const sectionForMeasure = getFloatingBoundSectionForElementOnPage(page, fid)
                const geometry = computeBottomPreferGeometryFromStoredLayout(el, pageIndex, sectionForMeasure)
                if (!geometry) continue
                if (!bottomPreferLayoutNeedsWrite(pos, geometry)) continue
                applyBottomPreferPositionSync(el, geometry, !!pos.preferRight)
              }
            }
          })
        })
      })
    },
    getElementSection(elementId: string): string | null {
      // Check in headers
      for (const page of this.pages) {
        const headerSection = page.component?.options?.[PAGE_SECTIONS.HEADERS]
        if (headerSection?.children) {
          const found = this.findElementInChildren(elementId, headerSection.children, headerSection, PAGE_SECTIONS.HEADERS)
          if (found) {
            return PAGE_SECTIONS.HEADERS
          }
        }
      }

      // Check in content (main page children)
      for (const page of this.pages) {
        if (page.children) {
          const found = this.findElementInChildren(elementId, page.children, page, PAGE_SECTIONS.CONTENT)
          if (found) {
            return PAGE_SECTIONS.CONTENT
          }
        }
      }

      // Check in footers
      for (const page of this.pages) {
        const footerSection = page.component?.options?.[PAGE_SECTIONS.FOOTERS]
        if (footerSection?.children) {
          const found = this.findElementInChildren(elementId, footerSection.children, footerSection, PAGE_SECTIONS.FOOTERS)
          if (found) {
            return PAGE_SECTIONS.FOOTERS
          }
        }
      }

      return null
    },
    updatePageNumberOptions(options: { enablePageNumbering?: boolean; pageNumberLocation?: string; pageNumberFormat?: string }) {
      if (!this.isDocumentEditable) return

      // Update store state
      if (options.enablePageNumbering !== undefined) {
        this.enablePageNumbering = options.enablePageNumbering
      }
      if (options.pageNumberLocation !== undefined) {
        this.pageNumberLocation = options.pageNumberLocation
      }
      if (options.pageNumberFormat !== undefined) {
        this.pageNumberFormat = options.pageNumberFormat
      }

      // Update all pages
      this.pages.forEach(page => {
        if (options.enablePageNumbering !== undefined) {
          this.updateElementOption({
            id: page.id,
            key: 'enablePageNumbering',
            value: options.enablePageNumbering,
            type: ELEMENTS_LOOKUP.PAGE,
          })
        }
        if (options.pageNumberLocation !== undefined) {
          this.updateElementOption({
            id: page.id,
            key: 'pageNumberLocation',
            value: options.pageNumberLocation,
            type: ELEMENTS_LOOKUP.PAGE,
          })
        }
        if (options.pageNumberFormat !== undefined) {
          this.updateElementOption({
            id: page.id,
            key: 'pageNumberFormat',
            value: options.pageNumberFormat,
            type: ELEMENTS_LOOKUP.PAGE,
          })
        }
      })

      this.createRedoStack()
    },
    /**
     * Persist measured header/footer heights for the given page (used for PDF page-break calculation).
     * Header/footer replace page margin; these values are saved so we don't rely only on DOM measurement.
     *
     * IMPORTANT: when header or footer height changes, `.page-content`'s height changes with it
     * (content area = pageHeight - headerHeight - footerHeight). Page-level floating elements
     * (signatures, text fields, etc. placed directly on `page.children`) have their `position`
     * stored RELATIVE to `.page-content` — so a shrinking content area strands any element whose
     * stored `top` now lands outside the new, smaller area. We rewrite those positions in place
     * so the element stays anchored to whichever edge it was originally placed near.
     */
    updatePageSectionHeights(pageId: string, heights: { headerHeight?: number; footerHeight?: number }) {
      const pageIndex = this.pages.findIndex(p => p.id === pageId)
      if (pageIndex === -1) return
      const page = this.pages[pageIndex]
      // Detect which side actually changed BEFORE we overwrite the stored heights so we can
      // tell the recompute helper to only nudge elements anchored to the moving edge. Without
      // this, a footer resize would also shift top-anchored fields (and vice versa) — even
      // though the opposite edge did not move.
      const prevHeaderH = Number(page.component.options.headerHeight)
      const prevFooterH = Number(page.component.options.footerHeight)
      if (heights.headerHeight !== undefined) {
        page.component.options.headerHeight = heights.headerHeight
      }
      if (heights.footerHeight !== undefined) {
        page.component.options.footerHeight = heights.footerHeight
      }
      const headerChanged =
        heights.headerHeight !== undefined && (!Number.isFinite(prevHeaderH) || Math.abs(Number(heights.headerHeight) - prevHeaderH) > 0.5)
      const footerChanged =
        heights.footerHeight !== undefined && (!Number.isFinite(prevFooterH) || Math.abs(Number(heights.footerHeight) - prevFooterH) > 0.5)
      if (headerChanged || footerChanged) {
        recomputePageLevelFloatingPositionsForContentResize(page, { headerChanged, footerChanged })
      }
      this.validateSectionHeights()
    },
    /**
     * Validate that header + footer combined height does not exceed page content area (960px).
     * Only runs when header or footer is enabled. Sets sectionHeightValidationError for UI/toast.
     */
    validateSectionHeights() {
      if (!this.headerSelected && !this.footerSelected) {
        this.sectionHeightValidationError = null
        return
      }
      const firstPage = this.pages[0]
      if (!firstPage?.component?.options) {
        this.sectionHeightValidationError = null
        return
      }
      const headerHeight = Number((this.headerSelected && firstPage.component.options.headerHeight) ?? 0) || 0
      const footerHeight = Number((this.footerSelected && firstPage.component.options.footerHeight) ?? 0) || 0
      const combined = headerHeight + footerHeight
      if (combined > MAX_HEADER_FOOTER_COMBINED_HEIGHT) {
        this.sectionHeightValidationError = 'validations.invalid.headerFooterCombinedSizeExceeded'
      } else {
        this.sectionHeightValidationError = null
      }
    },
  },
})
